import React, { useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import {
  array,
  arrayOf,
  bool,
  func,
  shape,
  string,
  oneOf,
  object,
} from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  propTypes,
} from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import { convertMoneyToNumber, unitDivisor } from '../../util/currency';
import { obfuscatedCoordinates } from '../../util/maps';
import { offsetByMiles } from '../../util/uiHelpers';
import {
  renderRichDescription,
  dropDanglingTokens,
} from '../../util/richDescription';
import {
  ensureCurrentUser,
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { hasMarketplaceAccess, hasMembershipHistory, needsBackgroundCheckFlow } from '../../util/userHelpers';
import { richText } from '../../util/richText';
import { orderImagesByCoverPhoto } from '../../util/listingImages';
import {
  INQUIRY_PROCESS_NAME,
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  manageDisableScrolling,
  isScrollingDisabled,
} from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  Modal,
  ExternalLink,
  Map,
  AvatarMedium,
  ResponsiveImage,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import InquiryForm from './InquiryForm/InquiryForm';

import css from './ListingPage.module.css';
import {
  ageSuitability,
  taxanomyIcons as defaultTaxanomyIcons,
} from '../../config/configListing.js';
import { amenityCategories } from '../../config/configAmenities';
import { surroundingAreaCategories } from '../EditListingPage/EditListingWizard/EditListingSurroundingAreaPanel/surroundingAreaConfig';
import { preferencesAndCareCategories } from '../EditListingPage/EditListingWizard/EditListingPreferencesPanel/preferencesAndCareConfig';
import { DEFAULT_PAYMENT_TYPES } from '../EditListingPage/EditListingWizard/EditListingPricingPanel/AcceptedPaymentTypesAccordion';
import { CANCELLATION_POLICIES } from '../EditListingPage/EditListingWizard/EditListingPricingPanel/CancellationPolicyAccordion';
import {
  initiateOrder,
  sendMessage,
} from '../CheckoutPage/CheckoutPage.duck.js';
import { makeTransition } from '../TransactionPage/TransactionPage.duck.js';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import {
  customTaxanomyIcon,
  getTaxanomyIconForDisplay,
  getTaxanomyIconOptions,
} from '../../util/taxanomyIcons';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID, Money } = sdkTypes;

const emptyValue = '-';
const descriptionPreviewLength = 170;
const previewListCount = 3;
const visibleAmenityCount = 10;
const normalizeFavoriteId = value => (value ? String(value) : '');
const isImageIcon = value =>
  /^(https?:\/\/|\/\/|data:image\/|\/static\/)/.test(String(value || ''));
const imageCarouselVariants = [
  'scaled-small',
  'scaled-medium',
  'scaled-large',
  'scaled-xlarge',
];

const bedLabels = {
  king: 'king bed',
  queen: 'queen bed',
  double: 'double bed',
  single: 'single bed',
  bunk: 'bunk bed',
  sofa: 'sofa bed',
  trundle: 'trundle bed',
  crib: 'crib',
  other: 'other bed',
};

const bedOrder = {
  gold: ['king', 'bunk', 'crib'],
  silver: [],
  unranked: ['queen', 'double', 'single', 'sofa', 'trundle'],
  other: ['other'],
};

const amenityCategoryOrder = [
  'bathroom',
  'bedroom_and_laundry',
  'entertainment',
  'family',
  'heating_and_cooling',
  'internet_and_office',
  'kitchen_and_dining',
  'outdoor',
  'standout',
  'eco_friendly',
  'safety_and_property',
  'accessibility',
];

const amenityRankByCategory = {
  bathroom: {
    gold: ['bathtub'],
    silver: [],
  },
  bedroom_and_laundry: {
    gold: [
      'blackout_shades',
      'dryer',
      'room_darkening_shades',
      'starter_essentials_bedroom',
      'washer',
    ],
    silver: [],
  },
  entertainment: {
    gold: ['smart_tv'],
    silver: ['tv'],
  },
  family: {
    gold: ['crib', 'pack_n_play'],
    silver: ['kids_bikes'],
  },
  heating_and_cooling: {
    gold: ['air_conditioning'],
    silver: ['heating', 'indoor_fireplace'],
  },
  internet_and_office: {
    gold: ['dedicated_workspace', 'office', 'wifi'],
    silver: [],
  },
  kitchen_and_dining: {
    gold: ['dishwasher', 'kitchen', 'starter_essentials_kitchen'],
    silver: ['high_chair', 'kids_dishes_and_utensils', 'premium_coffee_maker'],
  },
  outdoor: {
    gold: [
      'backyard',
      'balcony',
      'free_parking',
      'outdoor_playground',
      'patio',
    ],
    silver: ['bbq', 'fire_pit', 'outdoor_dining_area'],
  },
  standout: {
    gold: [
      'car_access',
      'exercise_equipment',
      'fenced_yard',
      'home_gym',
      'pets_allowed',
      'playroom',
      'pool',
    ],
    silver: [],
  },
  eco_friendly: {
    gold: [],
    silver: [],
  },
  safety_and_property: {
    gold: [
      'smoke_detector',
      'carbon_monoxide_detector',
      'security_system',
      'fire_extinguisher',
      'first_aid_kit',
      'safe',
    ],
    silver: [],
  },
};

const surroundingGroupOrder = [
  'community-essentials',
  'local-experiences',
  'learning-and-childcare',
  'health-and-medical',
];

const nearbyPreviewGroupOrder = [
  'community-essentials',
  'local-experiences',
  'learning-and-childcare',
];

const surroundingRankByCategory = {
  'community-essentials': {
    gold: [
      'airport',
      'coffee-shop',
      'grocery-store',
      'organic-grocery-store',
      'coworking-space',
      'gym',
    ],
    silver: [],
  },
  'local-experiences': {
    gold: [
      'childrens-museum',
      'zoo',
      'national-park',
      'hiking',
      'library',
      'outdoor-playground',
      'indoor-playground',
      'farmers-market',
    ],
    silver: [],
  },
  'learning-and-childcare': {
    gold: [
      'babysitter',
      'flexible-schools',
      'day-camps',
      'daycare',
      'library-programs',
    ],
    silver: [],
  },
  'health-and-medical': {
    gold: [
      'hospital',
      'urgent-care',
      'pharmacy',
      'pediatrician',
      'veterinarian',
    ],
    silver: [],
  },
};

const nearbyPreviewCounts = {
  'community-essentials': previewListCount,
  'local-experiences': previewListCount,
  'learning-and-childcare': previewListCount,
  'health-and-medical': previewListCount,
};

const preferencesRank = {
  preferences: {
    gold: ['no-smoking', 'no-parties'],
    silver: [],
  },
  'care-requests': {
    gold: ['pet-sitting', 'houseplant-care', 'mail-collection'],
    silver: [],
  },
};

const visibleHighlightCounts = {
  preferences: previewListCount,
  safety: previewListCount,
  health: previewListCount,
};

const requiredPaymentTypes = [
  { id: 'venmo', label: 'Venmo' },
  { id: 'wise', label: 'Wise' },
  { id: 'revolut', label: 'Revolut' },
  { id: 'cash', label: 'Cash' },
];

const definedMonthlyCostTypes = [
  { ids: ['mortgage'], label: 'Mortgage' },
  { ids: ['electricity'], label: 'Electricity' },
  { ids: ['gas'], label: 'Gas' },
  { ids: ['internet'], label: 'Internet' },
  { ids: ['lawn_care'], label: 'Lawn care' },
  { ids: ['car_payment'], label: 'Car payment' },
  { ids: ['pool_maintenance'], label: 'Pool maintenance' },
  { ids: ['trash_removal'], label: 'Trash removal' },
  { ids: ['water'], label: 'Water' },
  { ids: ['weekly_housecleaning'], label: 'Weekly housecleaning' },
  { ids: ['bi_weekly_housecleaning'], label: 'Bi-weekly housecleaning' },
];

const titleCase = value =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());

// Sentence-cases a label: only the first word is capitalized, the rest are
// lowercased (e.g. "Mail Collection" -> "Mail collection", "No Parties" ->
// "No parties"). Words with intentional caps beyond the first letter are left
// untouched so acronyms/brand terms survive (e.g. "TV", "Smart TV", "WiFi").
const sentenceCase = value => {
  const label = String(value || '')
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!label) {
    return '';
  }
  let seenWord = false;
  return label
    .split(' ')
    .map(word => {
      const isFirstWord = !seenWord;
      seenWord = true;
      if (/[A-Z]/.test(word.slice(1))) {
        return word;
      }
      return isFirstWord
        ? `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`
        : word.toLowerCase();
    })
    .join(' ');
};

const getPositiveCount = value => {
  const count = Number.parseInt(value, 10);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

const normalizeBedName = (name, count) => {
  const label = String(name || '').toLowerCase();
  return count === 1 && label.endsWith('s') ? label.slice(0, -1) : label;
};

const toArray = value => {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
};

const formatLabel = value => {
  if (!value) {
    return emptyValue;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object') {
    return (
      value.name ||
      value.label ||
      value.title ||
      value.customName ||
      value.option ||
      emptyValue
    );
  }
  return titleCase(value);
};

const normalizeKey = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const selectedItemId = item =>
  typeof item === 'string' ? item : item?.id || item?.option || item?.value;

const trimText = value => {
  if (value === null || typeof value === 'undefined') {
    return '';
  }
  return String(value).trim();
};

// Host free text supports three toolbar features: bold, bullet list and hyperlink.
// The shared renderer keeps the host's own whitespace intact and turns those tokens
// into <strong>, <ul>/<li> and anchors.
const renderDescription = text =>
  renderRichDescription(text, {
    listClassName: css.descriptionList,
    listItemClassName: css.descriptionListItem,
  });

const firstText = (...values) => {
  return values.map(trimText).find(Boolean) || '';
};

const getHostFamilyLabel = (author, displayName) => {
  const profile = author?.attributes?.profile || {};
  const profilePublicData = profile.publicData || {};
  const explicitFamilyName = firstText(
    profilePublicData.familyName,
    profilePublicData.family_name,
    profilePublicData.familyLastName,
    profilePublicData.family_last_name,
    profile.lastName,
    profilePublicData.lastName
  );
  const fallbackName = firstText(displayName);
  const baseName = explicitFamilyName || fallbackName;

  if (!baseName) {
    return 'the host';
  }

  const withoutFamilySuffix = baseName
    .replace(/^the\s+/i, '')
    .replace(/\s+family$/i, '')
    .trim();
  const familyName = explicitFamilyName
    ? withoutFamilySuffix
    : withoutFamilySuffix
      .split(/\s+/)
      .filter(Boolean)
      .pop();

  return familyName ? `${familyName}` : 'the host';
};

const getRankIndex = (id, rankedIds = []) => {
  const exactIndex = rankedIds.indexOf(id);
  if (exactIndex >= 0) {
    return exactIndex;
  }

  const normalizedId = normalizeKey(id);
  return rankedIds.findIndex(
    rankedId => normalizeKey(rankedId) === normalizedId
  );
};

const getTierInfo = (id, rankConfig = {}, taxonomyIndex = 9999) => {
  const goldIndex = getRankIndex(id, rankConfig.gold || []);
  if (goldIndex >= 0) {
    return { tier: 0, order: goldIndex };
  }

  const silverIndex = getRankIndex(id, rankConfig.silver || []);
  if (silverIndex >= 0) {
    return { tier: 1, order: silverIndex };
  }

  return { tier: 2, order: taxonomyIndex >= 0 ? taxonomyIndex : 9999 };
};

const getSortableItemName = item =>
  trimText(item?.name || item?.label || item?.categoryName || item?.id);

const compareItemsByName = (first, second) =>
  getSortableItemName(first).localeCompare(
    getSortableItemName(second),
    undefined,
    {
      numeric: true,
      sensitivity: 'base',
    }
  );

const sortByRankThenTaxonomy = (items, rankConfig = {}, taxonomyItems = []) => {
  const taxonomyOrder = taxonomyItems.map(item => item.id);
  return [...items].sort((first, second) => {
    const firstTier = getTierInfo(
      first.id,
      rankConfig,
      taxonomyOrder.indexOf(first.id)
    );
    const secondTier = getTierInfo(
      second.id,
      rankConfig,
      taxonomyOrder.indexOf(second.id)
    );

    if (firstTier.tier !== secondTier.tier) {
      return firstTier.tier - secondTier.tier;
    }

    const nameComparison = compareItemsByName(first, second);
    return nameComparison || firstTier.order - secondTier.order;
  });
};

const sortItemsAlphabetically = items =>
  [...toArray(items)].sort(compareItemsByName);

const getRankedBedIndex = bedType => {
  const normalizedType = normalizeKey(bedType);
  const goldIndex = bedOrder.gold.indexOf(normalizedType);
  if (goldIndex >= 0) {
    return goldIndex;
  }
  const unrankedIndex = bedOrder.unranked.indexOf(normalizedType);
  if (unrankedIndex >= 0) {
    return bedOrder.gold.length + unrankedIndex;
  }
  const otherIndex = bedOrder.other.indexOf(normalizedType);
  return otherIndex >= 0 ? 100 + otherIndex : 99;
};

const getBedTypeDetails = publicData => {
  const legacyBedTypes = Array.isArray(publicData?.bedTypes)
    ? publicData.bedTypes
    : [];

  if (legacyBedTypes.length > 0) {
    return legacyBedTypes
      .map(item => ({
        name: item?.name,
        count: getPositiveCount(item?.count),
        type: normalizeKey(item?.id || item?.type || item?.name),
      }))
      .filter(item => item.name && item.count > 0)
      .sort((first, second) => {
        return getRankedBedIndex(first.type) - getRankedBedIndex(second.type);
      });
  }

  const bedroomConfigurations = Array.isArray(publicData?.bedroomConfigurations)
    ? publicData.bedroomConfigurations
    : [];

  const bedCounts = bedroomConfigurations.reduce((counts, bedroom) => {
    Object.entries(bedroom?.beds || {}).forEach(([bedType, count]) => {
      const positiveCount = getPositiveCount(count);
      if (positiveCount > 0) {
        const label =
          bedType === 'other' && bedroom?.otherDescription
            ? bedroom.otherDescription
            : bedLabels[bedType] || titleCase(bedType);
        const normalizedType = bedType === 'other' ? 'other' : bedType;
        counts[label] = {
          name: label,
          type: normalizedType,
          count: (counts[label]?.count || 0) + positiveCount,
        };
      }
    });
    return counts;
  }, {});

  return Object.values(bedCounts).sort((first, second) => {
    return getRankedBedIndex(first.type) - getRankedBedIndex(second.type);
  });
};

const getPositiveNumber = value => {
  const numericValue = Number.parseFloat(String(value || '').replace(/,/g, ''));
  return Number.isFinite(numericValue) && numericValue > 0
    ? numericValue
    : null;
};

export const formatCurrencyValue = value => {
  const numericValue = Number.parseFloat(
    String(value || '0').replace(/,/g, '')
  );
  return `$${Number.isFinite(numericValue) ? numericValue.toLocaleString('en-US') : '0'
    }`;
};

const formatCount = (value, singular, plural) => {
  const count = getPositiveNumber(value);
  if (!count) {
    return null;
  }
  const formattedCount = count.toLocaleString('en-US', {
    maximumFractionDigits: Number.isInteger(count) ? 0 : 1,
  });
  return `${formattedCount} ${count === 1 ? singular : plural}`;
};

const normalizeHomeSizeUnit = unit => {
  const normalizedUnit = trimText(unit);
  const normalizedKey = normalizeKey(normalizedUnit);

  if (!normalizedUnit) {
    return 'sq ft';
  }
  if (['ft2', 'ft²', 'sq_ft', 'sqft', 'square_feet'].includes(normalizedKey)) {
    return 'sq ft';
  }
  if (['m2', 'm²', 'sq_m', 'sqm', 'square_meters'].includes(normalizedKey)) {
    return 'sq m';
  }
  return normalizedUnit;
};

const getPetsLabel = publicData => {
  const petsAllowed = publicData?.pets_allowed;
  if (typeof petsAllowed === 'undefined' || petsAllowed === null) {
    return null;
  }

  const label = formatLabel(petsAllowed);
  const normalizedLabel = normalizeKey(label);

  if (['yes', 'allowed', 'pets_allowed', 'true'].includes(normalizedLabel)) {
    return 'Pets allowed';
  }
  if (
    ['no', 'not_allowed', 'pets_not_allowed', 'false'].includes(normalizedLabel)
  ) {
    return 'Pets not allowed';
  }
  return label !== emptyValue ? label : null;
};

const hasCarAccess = publicData => {
  const carDetails = [
    ...toArray(publicData?.carDetails),
    ...toArray(publicData?.carAccess),
  ].filter(Boolean);
  const transportationCarCount = getPositiveNumber(
    publicData?.transportCarsCounts
  );
  const selectedCarAmenity = toArray(
    publicData?.selectedAmenities?.standout
  ).some(item => selectedItemId(item) === 'car_access');
  const transportationCar = toArray(publicData?.transportationAvailable).some(
    item => {
      const itemKey = normalizeKey(item?.id || item?.type || item?.name);
      return (
        ['car', 'cars', 'car_access'].includes(itemKey) &&
        getPositiveNumber(item?.count)
      );
    }
  );

  return (
    carDetails.length > 0 ||
    !!transportationCarCount ||
    selectedCarAmenity ||
    transportationCar
  );
};

const getInitials = name =>
  String(name || 'GFHR')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();

const regionSuffixes = [
  'Australian Capital Territory',
  'New South Wales',
  'Northern Territory',
  'Queensland',
  'South Australia',
  'Tasmania',
  'Victoria',
  'Western Australia',
  'ACT',
  'NSW',
  'NT',
  'QLD',
  'SA',
  'TAS',
  'VIC',
  'WA',
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WV',
  'WI',
  'WY',
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Northwest Territories',
  'Nova Scotia',
  'Nunavut',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Yukon',
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'NU',
  'ON',
  'PE',
  'QC',
  'SK',
  'YT',
];

const escapeRegExp = value =>
  String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const stripPostalCode = value =>
  String(value || '')
    .replace(/\b[A-Z]\d[A-Z]\s?\d[A-Z]\d\b/gi, '')
    .replace(/\b\d{5}(?:-\d{4})?\b/g, '')
    .replace(/\b\d{2,3}[\s-]\d{2,3}\b/g, '')
    .replace(/\b\d{4,6}\b/g, '')
    .replace(/\b[A-Z]\d{2}\s?[A-Z0-9]{4}\b/gi, '')
    .replace(/\b(?=[A-Z0-9]*\d)(?=[A-Z0-9]*[A-Z])[A-Z0-9]{3,8}\b/gi, '')
    .replace(/\s+/g, ' ')
    .replace(/[ ,]+$/, '')
    .trim();

const parseCityRegionSegment = segment => {
  const cleanSegment = stripPostalCode(segment);

  if (!cleanSegment) {
    return null;
  }

  const matchedRegion = [...regionSuffixes]
    .sort((first, second) => second.length - first.length)
    .find(region => {
      const regionPattern = new RegExp(
        `(?:^|\\s)${escapeRegExp(region)}$`,
        'i'
      );
      return regionPattern.test(cleanSegment);
    });

  if (matchedRegion) {
    const city = cleanSegment
      .replace(new RegExp(`(?:^|\\s)${escapeRegExp(matchedRegion)}$`, 'i'), '')
      .trim();
    return city ? { city, region: matchedRegion } : null;
  }

  return null;
};

const getCityStateCountry = address => {
  if (!address) {
    return '';
  }

  const parts = String(address)
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

  if (parts.length >= 4) {
    const country = parts[parts.length - 1];
    const city = stripPostalCode(parts[parts.length - 3]);
    const stateOrRegion = stripPostalCode(parts[parts.length - 2]);

    return [city, stateOrRegion, country].filter(Boolean).join(', ');
  }

  if (parts.length === 3) {
    const country = parts[2];
    const parsedCityRegion = parseCityRegionSegment(parts[1]);

    if (parsedCityRegion) {
      return [parsedCityRegion.city, parsedCityRegion.region, country]
        .filter(Boolean)
        .join(', ');
    }

    return [stripPostalCode(parts[1]), country].filter(Boolean).join(', ');
  }

  if (parts.length === 2) {
    const parsedCityRegion = parseCityRegionSegment(parts[0]);

    if (parsedCityRegion) {
      return [parsedCityRegion.city, parsedCityRegion.region, parts[1]]
        .filter(Boolean)
        .join(', ');
    }
  }

  return parts.length === 2 ? parts.join(', ') : '';
};

const getLocationLabel = address => {
  if (!address) {
    return 'Location not added';
  }

  return getCityStateCountry(address) || String(address).trim();
};

const getImageUrlFromEntity = (image, variantPrefix) => {
  const variants = image?.attributes?.variants || {};
  const variant =
    variants[`${variantPrefix}-2x`] ||
    variants[variantPrefix] ||
    variants['listing-card-large'] ||
    variants['listing-card'] ||
    variants['listing-card-2x'] ||
    Object.values(variants)[0];
  return variant?.url || null;
};

const getPhotoDescription = (publicData, image, index) => {
  const descriptions = Array.isArray(publicData?.imageDescriptions)
    ? publicData.imageDescriptions
    : [];
  const description =
    descriptions[index] || image?.description || image?.caption || image?.alt;

  return typeof description === 'string' ? description.trim() : '';
};

const getCloudinaryImageItems = publicData => {
  const cloudinaryImages = Array.isArray(publicData?.cloudnaryImage)
    ? publicData.cloudnaryImage
    : [];

  const orderedImages = orderImagesByCoverPhoto(
    cloudinaryImages.map((image, index) => ({ image, index })),
    publicData
  );

  return orderedImages
    .map(({ image, index }) => ({
      url: image?.secure_url || image?.url,
      description: getPhotoDescription(publicData, image, index),
    }))
    .filter(image => image.url);
};

const getGalleryImageItems = (publicData, images, variantPrefix) => {
  const cloudinaryImages = getCloudinaryImageItems(publicData);
  if (cloudinaryImages.length) {
    return cloudinaryImages;
  }

  const orderedImages = orderImagesByCoverPhoto(
    (images || []).map((image, index) => ({ image, index })),
    publicData
  );

  return orderedImages
    .map(({ image, index }) => ({
      url: getImageUrlFromEntity(image, variantPrefix),
      description: getPhotoDescription(publicData, image, index),
    }))
    .filter(image => image.url);
};

const getListingStats = publicData => {
  const bedCount = getBedTypeDetails(publicData).reduce(
    (sum, bed) => sum + bed.count,
    0
  );
  const homeSize = getPositiveNumber(publicData?.homeSize);
  const homeSizeLabel = homeSize
    ? `${homeSize.toLocaleString('en-US', {
      maximumFractionDigits: Number.isInteger(homeSize) ? 0 : 1,
    })} ${normalizeHomeSizeUnit(publicData?.homeSizeUnit)}`
    : null;

  return [
    formatCount(publicData?.maximumGuests, 'Guest', 'Guests'),
    formatCount(publicData?.NumberofBedrooms, 'Bedroom', 'Bedrooms'),
    bedCount > 0 ? formatCount(bedCount, 'Bed', 'Beds') : null,
    formatCount(publicData?.NumberofBathrooms, 'Bath', 'Baths'),
    homeSizeLabel,
    getPetsLabel(publicData),
    hasCarAccess(publicData) ? 'Car access' : null,
  ].filter(Boolean);
};

const getGroupedItems = (
  selectedItems,
  categories,
  itemKey = 'items',
  rankByCategory = {}
) => {
  return categories.map(category => {
    const selected = toArray(selectedItems?.[category.id]);
    const selectedIds = selected.map(selectedItemId).filter(Boolean);
    const selectedDataById = selected.reduce((dataById, item) => {
      const id = selectedItemId(item);
      return id
        ? {
          ...dataById,
          [id]: typeof item === 'object' ? item : { id },
        }
        : dataById;
    }, {});
    const itemOptions = category[itemKey] || [];
    const optionById = itemOptions.reduce(
      (options, item) => ({ ...options, [item.id]: item }),
      {}
    );

    const taxonomyItems = itemOptions
      .filter(option => selectedIds.includes(option.id))
      .map(option => ({
        id: option.id,
        name: option.name,
        icon: option.icon,
        isCustom: false,
        config: option,
        selectedData: selectedDataById[option.id] || {},
      }));

    const customItems = selected
      .filter(item => {
        const id = selectedItemId(item);
        return id && !optionById[id];
      })
      .map(item => {
        const id = selectedItemId(item);
        const itemData = typeof item === 'object' ? item : {};
        return {
          ...itemData,
          id,
          name: formatLabel(item?.name || item?.label || id),
          icon: itemData.icon,
          isCustom: true,
          config: null,
          selectedData: itemData,
        };
      });

    const rankConfig = rankByCategory[category.id] || {};
    const items = sortByRankThenTaxonomy(
      [...taxonomyItems, ...customItems],
      rankConfig,
      itemOptions
    ).map(item => {
      const taxonomyIndex = itemOptions.findIndex(
        option => option.id === item.id
      );
      const tierInfo = getTierInfo(item.id, rankConfig, taxonomyIndex);
      return {
        ...item,
        categoryId: category.id,
        categoryName: category.name,
        tier: tierInfo.tier,
        categoryOrder: categories.findIndex(cat => cat.id === category.id),
        taxonomyOrder: tierInfo.order,
      };
    });

    return {
      id: category.id,
      name: category.name,
      items: items.filter(item => item.name && item.name !== emptyValue),
    };
  });
};

const orderCategories = (categories, categoryOrder) =>
  [...categories].sort((first, second) => {
    const firstIndex = categoryOrder.indexOf(first.id);
    const secondIndex = categoryOrder.indexOf(second.id);
    return (
      (firstIndex >= 0 ? firstIndex : 999) -
      (secondIndex >= 0 ? secondIndex : 999)
    );
  });

const getLegacyAmenitySelections = publicData => ({
  family: publicData?.familyAmenities || [],
  safety_and_property: publicData?.safetyAmenities || [],
  standout: publicData?.standoutAmenities || [],
  eco_friendly: publicData?.ecoFriendlyAmenities || [],
  bathroom: publicData?.bathroomAmenities || [],
  bedroom_and_laundry: publicData?.bedroomAndLaundryAmenities || [],
  entertainment: publicData?.entertainmentAmenities || [],
  heating_and_cooling: publicData?.heatingAndCoolingAmenities || [],
  internet_and_office: publicData?.internetAndOfficeAmenities || [],
  kitchen_and_dining: publicData?.kitchenAndDiningAmenities || [],
  outdoor: publicData?.outdoorAmenities || [],
  accessibility: publicData?.accessibilityAmenities || [],
});

const otherAmenityDetailKeysByCategory = {
  bathroom: 'bathroomAmenitiesOther',
  bedroom_and_laundry: 'bedroomAndLaundryAmenitiesOther',
  entertainment: 'entertainmentAmenitiesOther',
  standout: 'standoutAmenitiesOther',
  heating_and_cooling: 'heatingAndCoolingAmenitiesOther',
  internet_and_office: 'internetAndOfficeAmenitiesOther',
  kitchen_and_dining: 'kitchenAndDiningAmenitiesOther',
  outdoor: 'outdoorAmenitiesOther',
  eco_friendly: 'ecoFriendlyAmenitiesOther',
  family: 'familyAmenitiesOther',
  safety_and_property: 'safetyAmenitiesOther',
  accessibility: 'accessibilityAmenitiesOther',
  practical: 'PracticalAmenitiesOther',
};

const getCarDetailItems = publicData => [
  ...toArray(publicData?.carDetails),
  ...toArray(publicData?.carAccess),
];

const getCarDetailLines = publicData =>
  getCarDetailItems(publicData)
    .map(car => {
      if (!car || typeof car !== 'object') {
        return null;
      }

      const model = firstText(
        car.yearMakeModel,
        car.makeModel,
        car.model,
        car.name,
        [car.year, car.make, car.modelName].filter(Boolean).join(' ')
      );
      const seats = firstText(car.seats, car.seatCount);
      const transmission = firstText(car.transmission);
      const seatLabel = seats ? `${seats} Seats` : '';
      const transmissionLabel = transmission ? titleCase(transmission) : '';

      return [model, seatLabel, transmissionLabel].filter(Boolean).join(' | ');
    })
    .filter((line, index, lines) => line && lines.indexOf(line) === index);

const removeCarAccessFromStandout = (selectedAmenities, publicData) => {
  if (getCarDetailLines(publicData).length === 0) {
    return selectedAmenities;
  }

  const selectedStandoutAmenities = toArray(selectedAmenities?.standout);
  return {
    ...selectedAmenities,
    standout: selectedStandoutAmenities.filter(
      item => selectedItemId(item) !== 'car_access'
    ),
  };
};

const formatAmenityOption = value =>
  typeof value === 'string' ? titleCase(value) : formatLabel(value);

const getAmenityOptionLine = (publicData, item) => {
  const selectedOptions = toArray(publicData?.amenityOptions?.[item.id]).filter(
    Boolean
  );
  const otherValue = trimText(publicData?.amenityOtherValues?.[item.id]);

  return selectedOptions.length
    ? selectedOptions
      .map(option =>
        normalizeKey(option) === 'other' && otherValue
          ? otherValue
          : formatAmenityOption(option)
      )
      .join(', ')
    : '';
};

const formatSpeedValue = value => {
  const text = trimText(value).replace(/\s*mbps$/i, '');
  return text ? `${text} Mbps` : '';
};

const getWifiDetailLine = publicData => {
  const wifiSpeeds = publicData?.wifiSpeeds || {};
  const download = firstText(
    wifiSpeeds.download,
    wifiSpeeds.downloadMbps,
    publicData?.downloadMbps,
    publicData?.wifiDownloadSpeed,
    publicData?.wifiDownloadMbps
  );
  const upload = firstText(
    wifiSpeeds.upload,
    wifiSpeeds.uploadMbps,
    publicData?.uploadMbps,
    publicData?.wifiUploadSpeed,
    publicData?.wifiUploadMbps
  );
  const downloadLabel = formatSpeedValue(download);
  const uploadLabel = formatSpeedValue(upload);

  if (downloadLabel && uploadLabel) {
    return `↓ ${downloadLabel} / ↑ ${uploadLabel}`;
  }
  if (downloadLabel) {
    return `↓ ${downloadLabel}`;
  }
  if (uploadLabel) {
    return `↑ ${uploadLabel}`;
  }
  return '';
};

const getSelectedAmenityDetailLine = item => {
  const data = item.selectedData || {};
  const detail = firstText(
    data.subtext,
    data.detail,
    data.details,
    data.description,
    data.customValue,
    data.customDescription,
    data.note
  );

  return detail && detail !== item.name ? detail : '';
};

const getAmenityOtherDetailLine = (publicData, item) => {
  const isOtherAmenity = normalizeKey(item.id) === 'other';
  const detailKey = otherAmenityDetailKeysByCategory[item.categoryId];
  return isOtherAmenity && detailKey ? trimText(publicData?.[detailKey]) : '';
};

const getAmenityDetailLines = (publicData, item) => {
  const specialLines =
    item.id === 'wifi' ? [getWifiDetailLine(publicData)] : [];

  return [
    ...specialLines,
    getAmenityOptionLine(publicData, item),
    getSelectedAmenityDetailLine(item),
    getAmenityOtherDetailLine(publicData, item),
  ].filter((line, index, lines) => line && lines.indexOf(line) === index);
};

const addCarAccessGroup = (groups, publicData) => {
  const carLines = getCarDetailLines(publicData);
  if (!carLines.length) {
    return groups;
  }

  const standoutIndex = groups.findIndex(group => group.id === 'standout');
  const insertIndex = standoutIndex >= 0 ? standoutIndex + 1 : groups.length;
  const carGroupOrder =
    standoutIndex >= 0
      ? groups[standoutIndex].items?.[0]?.categoryOrder + 0.5
      : insertIndex;
  const carAccessGroup = {
    id: 'car_access_details',
    name: 'Car access',
    items: [
      {
        id: 'car_access',
        name: 'Car access',
        icon: 'car',
        config: null,
        categoryId: 'car_access_details',
        categoryName: 'Car access',
        tier: 0,
        categoryOrder: Number.isFinite(carGroupOrder)
          ? carGroupOrder
          : insertIndex,
        taxonomyOrder: 0,
        detailLines: carLines,
        subtext: carLines[0],
      },
    ],
  };

  return [
    ...groups.slice(0, insertIndex),
    carAccessGroup,
    ...groups.slice(insertIndex),
  ];
};

const getAmenityGroups = publicData => {
  const selectedAmenities =
    publicData?.selectedAmenities || getLegacyAmenitySelections(publicData);
  const selectedAmenitiesWithoutCarAccessInStandout = removeCarAccessFromStandout(
    selectedAmenities,
    publicData
  );

  const amenityGroups = getGroupedItems(
    selectedAmenitiesWithoutCarAccessInStandout,
    orderCategories(amenityCategories, amenityCategoryOrder),
    'amenities',
    amenityRankByCategory
  ).map(group => ({
    ...group,
    items: group.items.map(item => {
      const detailLines = getAmenityDetailLines(publicData, item);
      return {
        ...item,
        detailLines,
        subtext: detailLines[0] || item.subtext,
      };
    }),
  }));

  return addCarAccessGroup(amenityGroups, publicData);
};

const getSurroundingGroups = (publicData, taxanomyIconOptions = []) => {
  const surroundingArea = publicData?.surroundingArea || {};
  const groups = getGroupedItems(
    surroundingArea.selectedItems,
    orderCategories(surroundingAreaCategories, surroundingGroupOrder),
    'items',
    surroundingRankByCategory
  );

  return groups.map(group => {
    const items = group.items.map(item => {
      const distanceDetail =
        surroundingArea.distanceDetails?.[`${group.id}-${item.id}`] || {};
      const namedLocation =
        surroundingArea.namedLocations?.[`${group.id}-${item.id}`];
      const minutes = distanceDetail.minutes;
      const mode = distanceDetail.mode || 'drive';
      const requiresDistance =
        item.config?.requiresDistance || item.requiresDistance || !!minutes;
      const distance =
        requiresDistance && minutes ? `${minutes} min ${mode}` : '';
      const icon = getTaxanomyIconForDisplay(taxanomyIconOptions, item);
      return {
        ...item,
        icon,
        categoryName: item.config?.name || item.name,
        locationName: namedLocation,
        name: item.config?.name || item.name,
        subtext: namedLocation || '',
        distance,
      };
    });
    return { ...group, items };
  });
};

const getPreferenceGroups = publicData =>
  getGroupedItems(
    publicData?.preferencesAndCare?.selectedItems,
    preferencesAndCareCategories,
    'items',
    preferencesRank
  );

const withTaxanomyIcons = (items, taxanomyIconOptions) =>
  items.map(item => ({
    ...item,
    icon: getTaxanomyIconForDisplay(taxanomyIconOptions, item),
  }));

const getSafetyAmenityItems = publicData => {
  const safetyGroup = getAmenityGroups(publicData).find(
    group => group.id === 'safety_and_property'
  );
  return safetyGroup?.items || [];
};

const getBedroomCards = publicData => {
  const bedrooms = Array.isArray(publicData?.bedroomConfigurations)
    ? publicData.bedroomConfigurations
    : [];

  if (bedrooms.length === 0) {
    const beds = getBedTypeDetails(publicData);
    return beds.length
      ? [
        {
          title: 'Bedroom',
          summary: beds
            .map(
              bed => `${bed.count} ${normalizeBedName(bed.name, bed.count)}`
            )
            .join(', '),
          bedIconTypes: beds.flatMap(bed =>
            Array(Math.min(bed.count, 4)).fill(bed.type || 'other')
          ),
        },
      ]
      : [];
  }

  return bedrooms.map((bedroom, index) => {
    const bedEntries = Object.entries(bedroom.beds || {}).filter(
      ([, count]) => getPositiveCount(count) > 0
    );

    const bedSummary = bedEntries
      .map(([bedType, count]) => {
        const baseLabel =
          bedType === 'other' && bedroom.otherDescription
            ? bedroom.otherDescription
            : bedLabels[bedType] || titleCase(bedType);
        return `${count} ${normalizeBedName(baseLabel, count)}`;
      })
      .join(', ');

    return {
      title: `Bedroom ${index + 1}`,
      summary: bedSummary || emptyValue,
      bedIconTypes: bedEntries.flatMap(([bedType, count]) =>
        Array(Math.min(Number(count), 4)).fill(bedType)
      ),
    };
  });
};

export const getPaymentTypeLabels = publicData => {
  const configuredPayments = [
    ...requiredPaymentTypes,
    ...DEFAULT_PAYMENT_TYPES,
    ...toArray(publicData?.customPaymentTypes),
  ].reduce((payments, paymentType) => {
    if (paymentType?.id) {
      return {
        ...payments,
        [paymentType.id]: paymentType.label || paymentType.name,
      };
    }
    return payments;
  }, {});

  const selected =
    toArray(publicData?.selectedPaymentTypes).length > 0
      ? toArray(publicData?.selectedPaymentTypes)
      : toArray(publicData?.paymentPreferences);

  const selectedLabels = selected
    .map(paymentType => {
      const id =
        typeof paymentType === 'string'
          ? paymentType
          : paymentType?.id || paymentType?.option;
      if (id === 'other' && publicData?.paymentModeOther) {
        return titleCase(publicData.paymentModeOther);
      }
      return configuredPayments[id] || formatLabel(paymentType?.label || id);
    })
    .filter(label => label && label !== emptyValue);

  return selectedLabels.length
    ? selectedLabels
    : requiredPaymentTypes.map(paymentType => paymentType.label);
};

export const getCancellationPolicy = policyId =>
  CANCELLATION_POLICIES.find(policy => policy.id === policyId) || null;

const getCostLabelMap = publicData =>
  [
    ...toArray(publicData?.customMonthlyCosts),
    ...toArray(publicData?.selectedCosts),
  ].reduce((labels, costType) => {
    if (typeof costType === 'string') {
      return labels;
    }

    const rawKey = String(
      costType?.option || costType?.id || costType?.label || ''
    ).trim();
    const key = normalizeKey(rawKey);
    const label = costType?.label || costType?.name;

    return rawKey && label
      ? { ...labels, [rawKey]: label, [key]: label }
      : labels;
  }, {});

export const getMonthlyCostRows = publicData => {
  const costValues = publicData?.costValues || {};
  const costLabelMap = getCostLabelMap(publicData);
  const entries = Object.entries(costValues)
    .map(([key, value]) => ({
      key,
      normalizedKey: normalizeKey(key),
      label: sentenceCase(
        costLabelMap[key] || costLabelMap[normalizeKey(key)] || key
      ),
      numericValue:
        Number.parseFloat(String(value || '0').replace(/,/g, '')) || 0,
    }))
    .filter(row => row.numericValue > 0);

  const usedKeys = new Set();
  const definedRows = definedMonthlyCostTypes
    .map(type => {
      const matchingRows = entries.filter(row =>
        type.ids.includes(row.normalizedKey)
      );
      matchingRows.forEach(row => usedKeys.add(row.key));
      const total = matchingRows.reduce(
        (sum, row) => sum + row.numericValue,
        0
      );
      return total > 0 ? { label: type.label, numericValue: total } : null;
    })
    .filter(Boolean);

  const unusedRows = entries.filter(row => !usedKeys.has(row.key));
  const otherRows = [
    ...unusedRows.filter(row => row.normalizedKey === 'other'),
    ...unusedRows.filter(row => row.normalizedKey !== 'other'),
  ];
  const otherTotal = otherRows.reduce((sum, row) => sum + row.numericValue, 0);
  const total = [...definedRows, ...otherRows].reduce(
    (sum, row) => sum + row.numericValue,
    0
  );

  return {
    definedRows,
    otherRows,
    otherTotal,
    total,
  };
};

const getFeeAmount = fee => {
  if (!fee || !(fee.selected || fee.value || fee.customValue)) {
    return 0;
  }
  const amount = fee.customValue || fee.value;
  const numericAmount = Number.parseFloat(String(amount || '0').replace(/,/g, ''));
  return Number.isFinite(numericAmount) ? numericAmount : 0;
};

// Excludes the pet fee unless the booking request actually includes a pet,
// regardless of whether the listing has one configured.
export const getFilteredFees = (fees, pets) =>
  pets === 'yes'
    ? fees || {}
    : Object.fromEntries(
      Object.entries(fees || {}).filter(([key]) => key !== 'pet_fee')
    );

// Booking deposit = one month's rent plus the selected refundable/non-refundable
// home fees (security deposit, cleaning fee, pet fee).
export const getBookingDepositTotal = (publicData, price) => {
  const feesTotal = Object.values(publicData?.fees || {}).reduce(
    (sum, fee) => sum + getFeeAmount(fee),
    0
  );
  const monthlyRent = price ? Number(price.amount || 0) / 100 : 0;
  return feesTotal + monthlyRent;
};

// Total cost of stay = the booking's rent (from the transaction's estimated
// payin total) plus the selected home fees (minus the pet fee when the
// booking request doesn't include a pet).
export const getEstimatedTotalCostOfStay = (payinTotal, publicData, pets) => {
  if (!payinTotal) {
    return payinTotal;
  }
  const fees = getFilteredFees(publicData?.fees, pets);
  const feesTotal = Object.values(fees).reduce(
    (sum, fee) => sum + getFeeAmount(fee),
    0
  );
  return new Money(
    payinTotal.amount + Math.round(feesTotal * unitDivisor(payinTotal.currency)),
    payinTotal.currency
  );
};

// The "Total cost of stay" and "Booking deposit" the guest sees in the booking
// form are derived from the whole selected range, but a selection longer than
// 365 days is booked as a single, shorter Sharetribe transaction - so that
// transaction's own payinTotal no longer matches the price the guest agreed
// to. Both numbers are therefore frozen into the transaction's protectedData
// when the booking is requested, and read back from there on the transaction
// page (see getSavedBookingPrices).
export const getBookingPriceSnapshot = (payinTotal, listing, pets) => {
  const publicData = listing?.attributes?.publicData || {};
  const price = listing?.attributes?.price;
  const totalCostOfStay = getEstimatedTotalCostOfStay(payinTotal, publicData, pets);
  // Same monthly-rent conversion the summary cards use.
  const monthlyPrice = price
    ? new Money(price.amount * 100, price.currency || 'USD')
    : null;
  const depositTotal = getBookingDepositTotal(
    { ...publicData, fees: getFilteredFees(publicData?.fees, pets) },
    monthlyPrice
  );
  const hasDeposit = Number.isFinite(depositTotal);

  if (!totalCostOfStay && !hasDeposit) {
    return null;
  }

  return {
    totalCostOfStay: totalCostOfStay
      ? { amount: totalCostOfStay.amount, currency: totalCostOfStay.currency }
      : null,
    // Ready-made label for the notification email templates, which get money
    // in whole currency units and can't rescale a sub-unit amount themselves.
    totalCostOfStayLabel: totalCostOfStay
      ? (
          totalCostOfStay.amount / unitDivisor(totalCostOfStay.currency)
        ).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : null,
    // Kept in whole currency units, exactly as the deposit is displayed.
    bookingDeposit: hasDeposit ? depositTotal : null,
    currency: totalCostOfStay?.currency || price?.currency || null,
  };
};

// Reads back the prices saved by getBookingPriceSnapshot. Returns null for
// transactions created before this was stored, so callers can fall back to
// recomputing from the transaction's own payinTotal.
export const getSavedBookingPrices = protectedData => {
  const saved = protectedData?.bookingPrices;
  if (!saved) {
    return null;
  }
  const total = saved.totalCostOfStay;
  const currency = saved.currency || total?.currency || null;
  const deposit = Number.isFinite(saved.bookingDeposit) ? saved.bookingDeposit : null;

  return {
    totalCostOfStay:
      Number.isFinite(total?.amount) && total?.currency
        ? new Money(total.amount, total.currency)
        : null,
    bookingDeposit: deposit,
    depositLabel:
      deposit != null
        ? `$${deposit.toLocaleString('en-US')}${currency ? ` ${currency}` : ''}`
        : null,
  };
};

export const getFeeRows = (fees, price) => {
  const feeOrder = [
    // { key: 'booking_deposit', label: 'Booking deposit' },
    { key: 'security_deposit', label: 'Security deposit', defaultRefundable: 'refundable' },
    { key: 'cleaning_fee', label: 'End of stay cleaning fee' },
    { key: 'pet_fee', label: 'Pet fee' },
  ];

  const monthlyRent = price ? Number(price.amount * 100 || 0) / 100 : 0;
  const rentRow = {
    label: '1 month rent',
    value: monthlyRent ? formatCurrencyValue(monthlyRent) : emptyValue,
  };

  const feeRows = feeOrder
    .map(({ key, label, defaultRefundable }) => {
      const fee = fees?.[key] || {};
      if (fee && (fee.selected || fee.value || fee.customValue)) {
        const refundable =
          fee.refundable === true
            ? 'refundable'
            : fee.refundable === false
              ? 'non-refundable'
              : defaultRefundable || null;
        const amount = fee.customValue || fee.value;
        const formattedAmount = amount ? formatCurrencyValue(amount) : null;
        return {
          label: refundable ? `${label} (${refundable})` : label,
          value: formattedAmount || emptyValue,
        };
      }

      return null;
    })
    .filter(Boolean);

  return [rentRow, ...feeRows];
};

const getMapProps = (geolocation, publicData, mapsConfig, listingId) => {
  if (!geolocation || !mapsConfig) {
    return null;
  }

  const address = publicData?.location?.address || '';
  const shiftedLocation = offsetByMiles(geolocation.lat, geolocation.lng, 1);
  const cacheKey = listingId
    ? `${listingId.uuid}_${geolocation.lat}_${geolocation.lng}`
    : null;

  return mapsConfig.fuzzy.enabled
    ? {
      obfuscatedCenter: obfuscatedCoordinates(
        shiftedLocation,
        mapsConfig.fuzzy.offset,
        cacheKey
      ),
    }
    : { address, center: shiftedLocation };
};

const GalleryImage = props => {
  const { src, className, title, onClick, priority } = props;
  const content = src ? (
    <ResponsiveImage
      className={classNames(css.newGalleryImage, className)}
      url={src}
      variants={['width-limit-800']}
      alt={title}
      loading={priority ? 'eager' : 'lazy'}
      decoding={priority ? 'sync' : 'async'}
      fetchpriority={priority ? 'high' : undefined}
    />
  ) : (
    <div
      className={classNames(
        css.newGalleryImage,
        css.newGalleryPlaceholder,
        className
      )}
    />
  );

  return onClick ? (
    <button className={css.galleryImageButton} type="button" onClick={onClick}>
      {content}
    </button>
  ) : (
    content
  );
};

GalleryImage.defaultProps = {
  src: null,
  className: null,
  title: '',
  onClick: null,
};

GalleryImage.propTypes = {
  src: string,
  className: string,
  title: string,
  onClick: func,
};

const GridIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M3 3h5v5H3V3Zm9 0h5v5h-5V3ZM3 12h5v5H3v-5Zm9 0h5v5h-5v-5Z" />
  </svg>
);

const HeartIcon = ({ isActive = false }) => (
  <svg width="34" height="32" viewBox="0 0 34 32" aria-hidden="true">
    <path
      d="M16.99 2.04C18.4.73 20.26 0 22.19 0c2.09 0 4.08.86 5.54 2.37a7.96 7.96 0 0 1 0 11.24c-.96 1-1.93 2.02-2.89 3.04-1.97 2.09-3.95 4.19-6.01 6.18-.51.48-1.19.75-1.9.73a2.62 2.62 0 0 1-1.86-.82l-8.82-9.13a7.96 7.96 0 0 1 0-11.24A7.65 7.65 0 0 1 11.55 0c2.01-.06 3.97.68 5.44 2.04Z"
      fill={isActive ? '#712739' : '#ffffff'}
    />
    <path
      d="M22.19.5c1.83 0 3.58.7 4.91 1.96l.27.26a7.46 7.46 0 0 1 0 10.55c-.97 1-1.93 2.02-2.9 3.04-1.96 2.08-3.94 4.18-5.99 6.15l-.01.02a2.12 2.12 0 0 1-1.53.58 2.12 2.12 0 0 1-1.51-.66l-8.82-9.14a7.46 7.46 0 0 1 0-10.55A7.15 7.15 0 0 1 11.57.5c1.88-.06 3.7.63 5.08 1.91l.34.32.34-.32A7.16 7.16 0 0 1 22.19.5Z"
      fill="none"
      stroke={isActive ? '#fff' : '#712739'}
    />
  </svg>
);

HeartIcon.propTypes = {
  isActive: bool,
};

const SectionGlyph = ({ type }) => {
  if (type === 'safety') {
    return (
      <svg
        width="21"
        height="21"
        viewBox="0 0 21 21"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M17.7782 3.48428L10.8097 1.10947C10.7256 1.08076 10.6128 1.06641 10.5 1.06641C10.3872 1.06641 10.2744 1.08076 10.1903 1.10947L3.22178 3.48428C3.05156 3.5417 2.91211 3.73857 2.91211 3.91904V13.812C2.91211 13.9925 3.029 14.2304 3.17051 14.3432L10.2396 19.8516C10.3113 19.9069 10.4036 19.9356 10.4979 19.9356C10.5923 19.9356 10.6866 19.9069 10.7563 19.8516L17.8254 14.3432C17.9669 14.2324 18.0838 13.9945 18.0838 13.812V3.91904C18.0879 3.73857 17.9484 3.54375 17.7782 3.48428ZM14.2427 6.98701L9.88271 12.9896C9.85206 13.0316 9.81194 13.0657 9.76562 13.0893C9.71929 13.1128 9.66807 13.125 9.61611 13.125C9.56416 13.125 9.51293 13.1128 9.46661 13.0893C9.42028 13.0657 9.38016 13.0316 9.34951 12.9896L6.75732 9.42129C6.67939 9.3126 6.75732 9.16084 6.89062 9.16084H8.02266C8.12725 9.16084 8.22773 9.21211 8.28926 9.29619L9.61611 11.1214L12.7107 6.85986C12.7723 6.77578 12.8707 6.72451 12.9773 6.72451H14.1094C14.2427 6.72656 14.3206 6.87832 14.2427 6.98701Z"
          fill="#712739"
        />
      </svg>
    );
  }

  if (type === 'health') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="21"
        height="21"
        viewBox="0 0 21 21"
        fill="none"
        aria-hidden="true"
      >
        <g clipPath="url(#health_clip)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.35306 1.87908C7.14175 2.36908 6.85387 3.09402 6.66838 3.81633C5.08506 3.90383 3.93575 4.03945 3.13162 4.16764C1.87994 4.36714 1.03163 5.37952 0.910875 6.5857C0.789687 7.79627 0.65625 9.66877 0.65625 12.0313C0.65625 14.3942 0.789687 16.2663 0.910875 17.4768C1.03119 18.6826 1.87994 19.695 3.13162 19.8949C4.47913 20.1101 6.79525 20.3438 10.5 20.3438C14.2048 20.3438 16.5204 20.1101 17.8684 19.8949C19.1201 19.6954 19.9684 18.683 20.0891 17.4768C20.2103 16.2658 20.3438 14.3942 20.3438 12.0313C20.3438 9.66833 20.2103 7.79627 20.0891 6.5857C19.9688 5.37995 19.1201 4.36758 17.8684 4.16764C17.0642 4.03902 15.9149 3.90427 14.3321 3.81633C14.1466 3.09402 13.8587 2.36864 13.6474 1.87908C13.3595 1.21233 12.7614 0.672017 11.9709 0.547767C11.4841 0.473448 10.9924 0.43659 10.5 0.437517C9.90588 0.437517 9.40887 0.488267 9.02956 0.547767C8.239 0.672017 7.64094 1.21277 7.35306 1.87908ZM10.5 2.18752C10.0056 2.18752 9.59962 2.22952 9.30125 2.27677C9.17525 2.29645 9.03963 2.38745 8.96 2.57295C8.79343 2.95604 8.6441 3.34639 8.51244 3.74283C9.12494 3.72708 9.78746 3.71906 10.5 3.71877C11.2125 3.71906 11.8752 3.72708 12.488 3.74283C12.3565 3.3464 12.2073 2.95605 12.0409 2.57295C11.9608 2.38745 11.8252 2.29645 11.6987 2.27677C11.302 2.21652 10.9013 2.18668 10.5 2.18752ZM9.49375 16.345C9.0825 16.2895 8.81781 15.9368 8.8025 15.5216C8.78806 15.1257 8.77187 14.5482 8.76137 13.7703C8.17747 13.7633 7.59366 13.7496 7.01006 13.7292C6.59488 13.7139 6.24225 13.4492 6.18669 13.038C6.14337 12.7042 6.12276 12.3678 6.125 12.0313C6.125 11.6121 6.15169 11.2792 6.18625 11.0246C6.24181 10.6133 6.59444 10.3486 7.00962 10.3333C7.40556 10.3189 7.98306 10.3027 8.76094 10.2922C8.77144 9.51433 8.78762 8.93683 8.80206 8.54089C8.81738 8.1257 9.08206 7.77308 9.49331 7.71752C9.74794 7.68295 10.0809 7.65627 10.5 7.65627C10.9191 7.65627 11.2521 7.68295 11.5067 7.71752C11.9179 7.77308 12.1826 8.1257 12.1979 8.54089C12.2124 8.93683 12.2286 9.51433 12.2391 10.2922C13.0169 10.3027 13.5944 10.3189 13.9904 10.3333C14.4056 10.3486 14.7582 10.6133 14.8138 11.0246C14.8483 11.2792 14.875 11.6121 14.875 12.0313C14.875 12.4504 14.8483 12.7833 14.8138 13.038C14.7582 13.4492 14.4056 13.7139 13.9904 13.7292C13.5944 13.7436 13.0169 13.7598 12.2391 13.7703C12.232 14.3542 12.2183 14.938 12.1979 15.5216C12.1826 15.9368 11.9179 16.2895 11.5067 16.345C11.2521 16.3796 10.9191 16.4063 10.5 16.4063C10.0809 16.4063 9.74794 16.3796 9.49331 16.345H9.49375Z"
            fill="#712739"
          />
        </g>
        <defs>
          <clipPath id="health_clip">
            <rect width="21" height="21" fill="white" />
          </clipPath>
        </defs>
      </svg>
    );
  }

  // preferences
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="15"
      height="16"
      viewBox="0 0 15 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M4.39375 15.7608C4.02431 15.7608 3.70104 15.6486 3.42396 15.4243C3.14687 15.2 2.96215 14.9097 2.86979 14.5535L1.97917 11.0108H12.2708L11.3802 14.5535C11.2878 14.9097 11.1031 15.2 10.826 15.4243C10.549 15.6486 10.2257 15.7608 9.85625 15.7608H4.39375ZM7.125 4.67743C7.125 3.48993 7.52743 2.46076 8.33229 1.58993C9.13715 0.719097 10.1399 0.191319 11.3406 0.0065972C11.4066 -0.00659724 11.466 5.60346e-08 11.5187 0.0263889C11.5715 0.0527778 11.6243 0.0857639 11.6771 0.125347C11.7299 0.164931 11.7663 0.214278 11.7863 0.273389C11.8064 0.3325 11.8096 0.395306 11.7958 0.461806C11.6507 1.50417 11.2253 2.41115 10.5197 3.18276C9.81403 3.95438 8.94636 4.42621 7.91667 4.59826V6.26076H13.4583C13.6826 6.26076 13.8708 6.33676 14.0228 6.48876C14.1748 6.64076 14.2505 6.82865 14.25 7.05243V8.63576C14.25 9.07118 14.0951 9.44406 13.7853 9.75439C13.4755 10.0647 13.1026 10.2196 12.6667 10.2191H1.58333C1.14792 10.2191 0.775306 10.0642 0.4655 9.75439C0.155694 9.44458 0.000527778 9.07171 0 8.63576V7.05243C0 6.82813 0.0760001 6.64024 0.228 6.48876C0.38 6.33729 0.567889 6.26129 0.791667 6.26076H6.33333V4.59826C5.30417 4.42674 4.43676 3.95517 3.73112 3.18356C3.02549 2.41194 2.59983 1.50469 2.45417 0.461806C2.44097 0.395833 2.4444 0.333292 2.46446 0.274181C2.48451 0.215069 2.52067 0.165458 2.57292 0.125347C2.62517 0.0852362 2.67794 0.0522501 2.73125 0.0263889C2.78456 0.000527834 2.84393 -0.00606946 2.90938 0.0065972C4.11007 0.191319 5.11285 0.719097 5.91771 1.58993C6.72257 2.46076 7.125 3.48993 7.125 4.67743Z"
        fill="#712739"
      />
    </svg>
  );
};

SectionGlyph.defaultProps = {
  type: 'preferences',
};

SectionGlyph.propTypes = {
  type: string,
};

const BedIcon = ({ bedType }) => {
  const isSofa = bedType === 'sofa';
  const isBunk = ['bunk', 'trundle', 'crib'].includes(bedType);
  const isSingle = bedType === 'single';
  const isKing = ['king'].includes(bedType);

  if (isSofa) {
    return (
      <svg
        width="27"
        height="27"
        viewBox="0 0 27 27"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={css.bedIconSvg}
        aria-hidden="true"
      >
        <g clipPath="url(#sofa_clip)">
          <path
            d="M8.08398 14.9768H21.5518V20.9625H8.08398V14.9768Z"
            fill="#712739"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.184 4.50195C7.87797 4.50195 5.19824 7.18168 5.19824 10.4877V10.8872C6.94121 11.0728 8.38563 12.2561 8.95091 13.8546H20.6852C21.2501 12.2561 22.6952 11.0728 24.4378 10.8872V10.4877C24.4378 7.18168 21.7581 4.50195 18.4521 4.50195H11.184ZM22.7057 14.9747C23.1723 14.9517 23.6122 14.7503 23.9345 14.4122C24.2569 14.0742 24.437 13.6252 24.4378 13.158C24.0012 13.2543 23.6032 13.4786 23.2947 13.8022C22.9861 14.1258 22.7811 14.534 22.7057 14.9747ZM6.93036 14.9747C6.85499 14.534 6.64994 14.1258 6.34141 13.8022C6.03287 13.4786 5.63487 13.2543 5.19824 13.158C5.19902 13.6252 5.37919 14.0742 5.70154 14.4122C6.02389 14.7503 6.46379 14.9517 6.93036 14.9747Z"
            fill="#712739"
          />
          <path
            d="M1.3501 13.7882C1.3501 13.3097 1.54019 12.8507 1.87857 12.5124C2.21695 12.174 2.67588 11.9839 3.15442 11.9839H4.71706C5.61004 11.9839 6.46644 12.3386 7.09786 12.97C7.72929 13.6015 8.08403 14.4579 8.08403 15.3509V20.0538C8.07613 20.6864 7.81928 21.2904 7.36913 21.735C6.91897 22.1796 6.31176 22.4289 5.67908 22.4289C5.04639 22.4289 4.43919 22.1796 3.98903 21.735C3.53887 21.2904 3.28202 20.6864 3.27413 20.0538V18.2954C3.27413 17.3576 2.95764 16.4474 2.37553 15.7119L1.73954 14.9075C1.48732 14.589 1.35009 14.1945 1.3501 13.7882ZM28.2858 13.7882C28.2858 13.3097 28.0957 12.8507 27.7573 12.5124C27.419 12.174 26.96 11.9839 26.4815 11.9839H24.9188C24.0259 11.9839 23.1695 12.3386 22.538 12.97C21.9066 13.6015 21.5519 14.4579 21.5519 15.3509V20.0538C21.5598 20.6864 21.8166 21.2904 22.2668 21.735C22.7169 22.1796 23.3241 22.4289 23.9568 22.4289C24.5895 22.4289 25.1967 22.1796 25.6469 21.735C26.097 21.2904 26.3539 20.6864 26.3618 20.0538V18.2954C26.3618 17.3576 26.6783 16.4474 27.2604 15.7119L27.8964 14.9075C28.1486 14.589 28.2858 14.1945 28.2858 13.7882Z"
            fill="#712739"
          />
        </g>
        <defs>
          <clipPath id="sofa_clip">
            <rect width="27" height="27" fill="white" />
          </clipPath>
        </defs>
      </svg>
    );
  }

  if (isBunk) {
    return (
      <svg
        width="27"
        height="27"
        viewBox="0 0 27 27"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={css.bedIconSvg}
        aria-hidden="true"
      >
        <path
          d="M11.5287 22.2428H22.8215C22.8384 22.2429 22.8551 22.2396 22.8707 22.2331C22.8864 22.2267 22.9006 22.2172 22.9125 22.2053C22.9245 22.1933 22.9339 22.1791 22.9404 22.1635C22.9468 22.1479 22.9501 22.1311 22.9501 22.1142V20.3464C22.9501 17.9853 21.0361 16.0713 18.675 16.0713H11.9145C11.6303 16.0713 11.4001 16.3015 11.4001 16.5856V22.1139C11.4001 22.1851 11.4579 22.2428 11.5287 22.2428Z"
          fill="#712739"
          stroke="#712739"
          strokeWidth="2.025"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M4.05005 13.5V24.3M4.05005 22.2429H22.95V24.3"
          stroke="#712739"
          strokeWidth="2.025"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6.89525 17.6062C7.1684 17.4518 7.47685 17.3706 7.79064 17.3706C8.10443 17.3706 8.41288 17.4518 8.68602 17.6062L8.71404 17.6221C8.98579 17.7758 9.21186 17.9989 9.36916 18.2685C9.52646 18.5382 9.60935 18.8448 9.60935 19.157C9.60935 19.4692 9.52646 19.7758 9.36916 20.0455C9.21186 20.3152 8.98579 20.5383 8.71404 20.692L8.68602 20.7078C8.1305 21.0217 7.45077 21.0217 6.89559 20.7078L6.86724 20.692C6.59549 20.5383 6.36941 20.3152 6.21211 20.0455C6.05481 19.7758 5.97192 19.4692 5.97192 19.157C5.97192 18.8448 6.05481 18.5382 6.21211 18.2685C6.36941 17.9989 6.59549 17.7758 6.86724 17.6221L6.89525 17.6062Z"
          fill="#712739"
          stroke="#712739"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M11.5287 11.443H22.8215C22.8384 11.4431 22.8551 11.4398 22.8707 11.4333C22.8864 11.4269 22.9006 11.4174 22.9125 11.4054C22.9245 11.3935 22.9339 11.3793 22.9404 11.3637C22.9468 11.3481 22.9501 11.3313 22.9501 11.3144V9.5466C22.9501 7.18545 21.0361 5.27148 18.675 5.27148H11.9145C11.6303 5.27148 11.4001 5.50166 11.4001 5.78583V11.3141C11.4001 11.3853 11.4579 11.443 11.5287 11.443Z"
          fill="#712739"
          stroke="#712739"
          strokeWidth="2.025"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M4.05005 2.69995V13.5M4.05005 11.4429H22.95V13.5"
          stroke="#712739"
          strokeWidth="2.025"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6.89525 6.80642C7.1684 6.65197 7.47685 6.5708 7.79064 6.5708C8.10443 6.5708 8.41288 6.65197 8.68602 6.80642L8.71404 6.82228C8.98579 6.97598 9.21186 7.19906 9.36916 7.46874C9.52646 7.73842 9.60935 8.04503 9.60935 8.35723C9.60935 8.66943 9.52646 8.97604 9.36916 9.24572C9.21186 9.5154 8.98579 9.73849 8.71404 9.89218L8.68602 9.90804C8.1305 10.2219 7.45077 10.2219 6.89559 9.90804L6.86724 9.89218C6.59549 9.73849 6.36941 9.5154 6.21211 9.24572C6.05481 8.97604 5.97192 8.66943 5.97192 8.35723C5.97192 8.04503 6.05481 7.73842 6.21211 7.46874C6.36941 7.19906 6.59549 6.97598 6.86724 6.82228L6.89525 6.80642Z"
          fill="#712739"
          stroke="#712739"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M22.95 13.5V22.2429"
          stroke="#712739"
          strokeWidth="2.025"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (isSingle) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="27"
        height="27"
        viewBox="0 0 27 27"
        fill="none"
        className={css.bedIconSvg}
        aria-hidden="true"
      >
        <path
          d="M21.0018 10.5V6C21.0018 5.20435 20.6859 4.44129 20.1236 3.87868C19.5613 3.31607 18.7987 3 18.0035 3H9.00881C7.35978 3 6.01057 4.35 6.01057 6V10.5C4.36154 10.5 2.84743 11.85 3.01234 13.5V21H5.00616L6.01057 24H7.50969L8.5141 21H18.4982L19.5026 24H21.0018L22.0062 21H24V13.5C24 12.7044 23.6841 11.9413 23.1218 11.3787C22.5596 10.8161 21.7969 10.5 21.0018 10.5ZM18.0035 10.5H9.00881V6H18.0035V10.5Z"
          fill="#712739"
        />
      </svg>
    );
  }

  if (isKing) {
    return (
      <svg
        width="32"
        height="19"
        viewBox="0 0 32 19"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={css.bedIconSvg}
        aria-hidden="true"
      >
        <g clipPath="url(#king_clip)">
          <path
            d="M28.5714 8.13437C27.851 7.89531 27.0726 7.77215 26.2857 7.77273H5.71429C4.9275 7.77214 4.14914 7.89511 3.42857 8.13383C2.41014 8.47039 1.54353 9.02387 0.934639 9.72661C0.325752 10.4294 0.000973176 11.2509 0 12.0909V18.1364C0 18.3654 0.120408 18.5851 0.334735 18.747C0.549062 18.909 0.839753 19 1.14286 19C1.44596 19 1.73665 18.909 1.95098 18.747C2.16531 18.5851 2.28571 18.3654 2.28571 18.1364V17.7045C2.28757 17.5905 2.34837 17.4814 2.45513 17.4008C2.5619 17.3201 2.70617 17.2741 2.85714 17.2727H29.1429C29.2938 17.2741 29.4381 17.3201 29.5449 17.4008C29.6516 17.4814 29.7124 17.5905 29.7143 17.7045V18.1364C29.7143 18.3654 29.8347 18.5851 30.049 18.747C30.2633 18.909 30.554 19 30.8571 19C31.1602 19 31.4509 18.909 31.6653 18.747C31.8796 18.5851 32 18.3654 32 18.1364V12.0909C31.9989 11.251 31.674 10.4296 31.0652 9.72693C30.4563 9.02429 29.5897 8.4709 28.5714 8.13437ZM24.5714 0H7.42857C6.36771 0 5.35029 0.318465 4.60014 0.885336C3.85 1.45221 3.42857 2.22105 3.42857 3.02273V6.90909C3.4286 6.94255 3.43892 6.97554 3.45872 7.00547C3.47852 7.0354 3.50725 7.06144 3.54264 7.08154C3.57804 7.10164 3.61913 7.11525 3.66268 7.12129C3.70622 7.12734 3.75103 7.12565 3.79357 7.11636C4.41747 6.97847 5.06425 6.90867 5.71429 6.90909H6.01643C6.08695 6.90943 6.15515 6.89004 6.20789 6.85467C6.26064 6.81929 6.29421 6.77043 6.30214 6.71747C6.36448 6.29547 6.63033 5.90561 7.04892 5.62234C7.46752 5.33907 8.00953 5.18225 8.57143 5.18182H12.5714C13.1337 5.18185 13.6762 5.3385 14.0953 5.6218C14.5143 5.90511 14.7805 6.29519 14.8429 6.71747C14.8508 6.77043 14.8844 6.81929 14.9371 6.85467C14.9899 6.89004 15.058 6.90943 15.1286 6.90909H16.8757C16.9462 6.90943 17.0144 6.89004 17.0672 6.85467C17.1199 6.81929 17.1535 6.77043 17.1614 6.71747C17.2237 6.29575 17.4893 5.90611 17.9074 5.62288C18.3256 5.33965 18.867 5.18265 19.4286 5.18182H23.4286C23.9908 5.18185 24.5334 5.3385 24.9524 5.6218C25.3715 5.90511 25.6376 6.29519 25.7 6.71747C25.7079 6.77043 25.7415 6.81929 25.7942 6.85467C25.847 6.89004 25.9152 6.90943 25.9857 6.90909H26.2857C26.9358 6.90885 27.5826 6.97883 28.2064 7.1169C28.249 7.1262 28.2939 7.12788 28.3375 7.12181C28.3811 7.11574 28.4222 7.10208 28.4576 7.08192C28.4931 7.06176 28.5218 7.03565 28.5415 7.00565C28.5613 6.97565 28.5715 6.94259 28.5714 6.90909V3.02273C28.5714 2.22105 28.15 1.45221 27.3999 0.885336C26.6497 0.318465 25.6323 0 24.5714 0Z"
            fill="#712739"
          />
        </g>
        <defs>
          <clipPath id="king_clip">
            <rect width="32" height="19" fill="white" />
          </clipPath>
        </defs>
      </svg>
    );
  }

  // default: double / queen / simple bed
  return (
    <svg
      width="26"
      height="19"
      viewBox="0 0 26 19"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={css.bedIconSvg}
      aria-hidden="true"
    >
      <g clipPath="url(#simple_clip)">
        <path
          d="M23.2143 8.13437C22.6289 7.89531 21.9965 7.77215 21.3571 7.77273H4.64286C4.0036 7.77214 3.37118 7.89511 2.78571 8.13383C1.95824 8.47039 1.25411 9.02387 0.759394 9.72661C0.264673 10.4294 0.000790706 11.2509 0 12.0909V18.1364C0 18.3654 0.0978313 18.5851 0.271972 18.747C0.446113 18.909 0.682299 19 0.928571 19C1.17484 19 1.41103 18.909 1.58517 18.747C1.75931 18.5851 1.85714 18.3654 1.85714 18.1364V17.7045C1.85865 17.5905 1.90805 17.4814 1.99479 17.4008C2.08154 17.3201 2.19876 17.2741 2.32143 17.2727H23.6786C23.8012 17.2741 23.9185 17.3201 24.0052 17.4008C24.092 17.4814 24.1414 17.5905 24.1429 17.7045V18.1364C24.1429 18.3654 24.2407 18.5851 24.4148 18.747C24.589 18.909 24.8252 19 25.0714 19C25.3177 19 25.5539 18.909 25.728 18.747C25.9022 18.5851 26 18.3654 26 18.1364V12.0909C25.9991 11.251 25.7352 10.4296 25.2404 9.72693C24.7457 9.02429 24.0417 8.4709 23.2143 8.13437ZM19.9643 0H6.03571C5.17376 0 4.34711 0.318465 3.73762 0.885336C3.12812 1.45221 2.78571 2.22105 2.78571 3.02273V6.90909C2.78574 6.94255 2.79413 6.97554 2.81021 7.00547C2.8263 7.0354 2.84964 7.06144 2.8784 7.08154C2.90716 7.10164 2.94054 7.11525 2.97593 7.12129C3.01131 7.12734 3.04772 7.12565 3.08228 7.11636C3.58919 6.97847 4.1147 6.90867 4.64286 6.90909H4.88835C4.94565 6.90943 5.00106 6.89004 5.04391 6.85467C5.08677 6.81929 5.11405 6.77043 5.12049 6.71747C5.17114 6.29547 5.38714 5.90561 5.72725 5.62234C6.06736 5.33907 6.50774 5.18225 6.96429 5.18182H10.2143C10.6711 5.18185 11.1119 5.3385 11.4524 5.6218C11.7929 5.90511 12.0091 6.29519 12.0598 6.71747C12.0663 6.77043 12.0935 6.81929 12.1364 6.85467C12.1793 6.89004 12.2347 6.90943 12.292 6.90909H13.7115C13.7688 6.90943 13.8242 6.89004 13.8671 6.85467C13.9099 6.81929 13.9372 6.77043 13.9437 6.71747C13.9943 6.29575 14.21 5.90611 14.5498 5.62288C14.8895 5.33965 15.3295 5.18265 15.7857 5.18182H19.0357C19.4926 5.18185 19.9334 5.3385 20.2738 5.6218C20.6143 5.90511 20.8306 6.29519 20.8812 6.71747C20.8877 6.77043 20.915 6.81929 20.9578 6.85467C21.0007 6.89004 21.0561 6.90943 21.1134 6.90909H21.3571C21.8853 6.90885 22.4108 6.97883 22.9177 7.1169C22.9523 7.1262 22.9888 7.12788 23.0242 7.12181C23.0596 7.11574 23.0931 7.10208 23.1218 7.08192C23.1506 7.06176 23.1739 7.03565 23.19 7.00565C23.206 6.97565 23.2143 6.94259 23.2143 6.90909V3.02273C23.2143 2.22105 22.8719 1.45221 22.2624 0.885336C21.6529 0.318465 20.8262 0 19.9643 0Z"
          fill="#712739"
        />
      </g>
      <defs>
        <clipPath id="simple_clip">
          <rect width="26" height="19" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

BedIcon.defaultProps = {
  bedType: 'king',
};

BedIcon.propTypes = {
  bedType: string,
};

const AmenityIcon = props => {
  const { icon, isCustom, emptyIfMissing } = props;
  const iconKey = normalizeKey(icon);

  if (isCustom) {
    return (
      <span className={css.amenityIcon} aria-hidden="true">
        <img src={customTaxanomyIcon} alt="" />
      </span>
    );
  }

  if (isImageIcon(icon)) {
    return (
      <span className={css.amenityIcon} aria-hidden="true">
        <img src={icon} alt="" />
      </span>
    );
  }

  if (emptyIfMissing && !icon) {
    return <span className={css.amenityIcon} aria-hidden="true" />;
  }

  if (iconKey === 'wifi') {
    return (
      <span className={css.amenityIcon} aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24">
          <path
            d="M4 9.5a11 11 0 0 1 16 0M7.5 13a6.2 6.2 0 0 1 9 0M10.6 16.2a2 2 0 0 1 2.8 0"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="12" cy="19" r="1.3" />
        </svg>
      </span>
    );
  }

  if (iconKey === 'car') {
    return (
      <span className={css.amenityIcon} aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24">
          <path
            d="M5 13l1.4-4.2A2.6 2.6 0 0 1 8.9 7h6.2a2.6 2.6 0 0 1 2.5 1.8L19 13v4H5v-4Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path
            d="M7 13h10"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="8" cy="17" r="1.5" />
          <circle cx="16" cy="17" r="1.5" />
        </svg>
      </span>
    );
  }

  if (iconKey === 'pool') {
    return (
      <span className={css.amenityIcon} aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24">
          <path
            d="M6 5h4v9M14 5h4v9M4 14c2.2 0 2.2-1.5 4.4-1.5S10.6 14 12.8 14s2.2-1.5 4.4-1.5S19.4 14 22 14M4 18c2.2 0 2.2-1.5 4.4-1.5S10.6 18 12.8 18s2.2-1.5 4.4-1.5S19.4 18 22 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }

  if (emptyIfMissing) {
    return <span className={css.amenityIcon} aria-hidden="true" />;
  }

  return (
    <span className={css.amenityIcon} aria-hidden="true">
      <svg width="20" height="20" viewBox="0 0 20 20">
        <path d="M7.8 14.4 3.9 10.5 5.1 9.3l2.7 2.7 7-7 1.3 1.2-8.3 8.2Z" />
      </svg>
    </span>
  );
};

const NearbyIcon = props => {
  const { icon, isCustom, emptyIfMissing } = props;

  return (
    <span className={css.nearbyItemIcon} aria-hidden="true">
      {isCustom ? (
        <img src={customTaxanomyIcon} alt="" />
      ) : icon ? (
        <img src={icon} alt="" />
      ) : emptyIfMissing ? null : (
        <svg width="18" height="18" viewBox="0 0 20 20">
          <path d="M10 2a6 6 0 0 0-6 6c0 4.5 6 10 6 10s6-5.5 6-10a6 6 0 0 0-6-6Zm0 8.5A2.5 2.5 0 1 1 10 5a2.5 2.5 0 0 1 0 5.5Z" />
        </svg>
      )}
    </span>
  );
};

NearbyIcon.defaultProps = {
  icon: null,
  isCustom: false,
  emptyIfMissing: false,
};

NearbyIcon.propTypes = {
  icon: string,
  isCustom: bool,
  emptyIfMissing: bool,
};

const DetailModal = props => {
  const { detailModal, onClose, onManageDisableScrolling } = props;
  const modalType = detailModal?.type;
  const isPolicy = modalType === 'policy';
  const sections =
    detailModal?.sections ||
    (detailModal?.items ? [{ id: 'items', items: detailModal.items }] : []);

  return (
    <Modal
      id="ListingPage.detailModal"
      scrollLayerClassName={css.detailModalScrollLayer}
      containerClassName={css.detailModalContainer}
      contentClassName={css.detailModalContentWrapper}
      isOpen={!!detailModal}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      closed
      usePortal
    >
      <article className={css.detailModalCard}>
        <button
          className={css.detailModalClose}
          type="button"
          onClick={onClose}
          aria-label="Close details"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="m6 6 12 12M18 6 6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <h2 className={css.detailModalTitle}>{detailModal?.title}</h2>

        {isPolicy ? (
          <div className={css.policyModalBody}>
            <strong className={css.policyModalLabel}>
              {detailModal?.policy?.label}
            </strong>
            <p>{detailModal?.policy?.description}</p>
            {(detailModal?.policy?.groups || []).map(group => (
              <div key={group.label} className={css.policyModalGroup}>
                <h3>{group.label}</h3>
                <ul>
                  {(group.items || []).map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <div className={css.detailModalBody}>
            {sections.map(section => (
              <div
                key={section.id || section.title}
                className={css.detailModalSection}
              >
                {section.title ? (
                  <h3 className={css.detailModalSectionTitle}>
                    {section.title}
                  </h3>
                ) : null}
                {(section.items || []).map(item => (
                  <div
                    key={`${section.id || 'section'}-${item.id}`}
                    className={css.detailModalRow}
                  >
                    {modalType === 'nearby' ? (
                      <NearbyIcon
                        icon={item.icon}
                        isCustom={item.isCustom}
                        emptyIfMissing
                      />
                    ) : (
                      <AmenityIcon
                        icon={item.icon}
                        isCustom={item.isCustom}
                        emptyIfMissing
                      />
                    )}
                    <div className={css.detailModalItemCopy}>
                      <span>{sentenceCase(item.name)}</span>
                      {item.detailLines?.length ? (
                        item.detailLines.map(line => (
                          <small key={line}>{line}</small>
                        ))
                      ) : item.subtext ? (
                        <small>{item.subtext}</small>
                      ) : null}
                    </div>
                    {item.distance ? (
                      <span className={css.detailModalDistance}>
                        {item.distance}
                      </span>
                    ) : null}
                  </div>
                ))}
                {section.note ? (
                  <div
                    className={classNames(
                      css.detailModalNote,
                      css.detailModalSectionNote
                    )}
                  >
                    <h4 className={css.detailModalNoteTitle}>
                      Additional description:
                    </h4>
                    {renderDescription(section.note)}
                  </div>
                ) : null}
              </div>
            ))}
            {detailModal?.note ? (
              <div className={css.detailModalNote}>
                {renderDescription(detailModal.note)}
              </div>
            ) : null}
          </div>
        )}
      </article>
    </Modal>
  );
};

DetailModal.defaultProps = {
  detailModal: null,
};

DetailModal.propTypes = {
  detailModal: object,
  onClose: func.isRequired,
  onManageDisableScrolling: func.isRequired,
};

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );

  const [isOpen, setOpen] = useState();
  const [checkSubscription, setCheckSubscription] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [photoModalTopOffset, setPhotoModalTopOffset] = useState(0);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [detailModal, setDetailModal] = useState(null);
  const [bedroomOffset, setBedroomOffset] = useState(0);
  const [bedroomMaxOffset, setBedroomMaxOffset] = useState(Infinity);
  const bedroomWrapperRef = React.useRef(null);

  // Matches --viewportMedium (768px) in src/styles/customMediaQueries.css.
  // Defaults to false (desktop) on the server and on first client render, so SSR output
  // and the initial client render always match; only updates after mount, client-side only.
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const updateIsMobile = () => setIsMobileViewport(mql.matches);
    updateIsMobile();
    mql.addEventListener('change', updateIsMobile);
    return () => mql.removeEventListener('change', updateIsMobile);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews,
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    onTransition,
    onSendMessage,
    onToggleFavorite,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    taxanomyIcons,
  } = props;

  const listingConfig = config.listing;
  const taxanomyIconOptions = useMemo(
    () => getTaxanomyIconOptions(taxanomyIcons, defaultTaxanomyIcons),
    [taxanomyIcons]
  );
  const handleCLick = value => {
    setOpen(value);
  };
  const showMore = section => {
    setExpandedSections(prev => ({ ...prev, [section]: true }));
  };
  const showLess = section => {
    setExpandedSections(prev => ({ ...prev, [section]: false }));
  };

  // Memoize the Marketplace_Access value to prevent unnecessary re-renders
  const marketplaceAccess = useMemo(() => {
    const publicData = currentUser?.attributes?.profile?.publicData || {};
    const protectedData = currentUser?.attributes?.profile?.protectedData || {};

    return hasMarketplaceAccess(publicData, protectedData);
  }, [
    currentUser?.attributes?.profile?.publicData,
    currentUser?.attributes?.profile?.protectedData,
  ]);
  const membershipHistory = useMemo(() => {
    const publicData = currentUser?.attributes?.profile?.publicData || {};
    const protectedData = currentUser?.attributes?.profile?.protectedData || {};

    return hasMembershipHistory(publicData, protectedData);
  }, [
    currentUser?.attributes?.profile?.publicData,
    currentUser?.attributes?.profile?.protectedData,
  ]);
  const backgroundCheckFlowPending = useMemo(() => {
    const publicData = currentUser?.attributes?.profile?.publicData || {};
    const protectedData = currentUser?.attributes?.profile?.protectedData || {};

    return needsBackgroundCheckFlow(publicData, protectedData);
  }, [
    currentUser?.attributes?.profile?.publicData,
    currentUser?.attributes?.profile?.protectedData,
  ]);

  useEffect(() => {
    if (!isAuthenticated) {
      setCheckSubscription(false);
    } else {
      setCheckSubscription(!marketplaceAccess && membershipHistory && !backgroundCheckFlowPending);
    }
  }, [isAuthenticated, marketplaceAccess, membershipHistory, backgroundCheckFlowPending]);

  const spaces = listingConfig.listingFields.filter(
    item => (item.key == item.key) == 'indoor_spaces'
  );
  const outdoorSpaces = listingConfig.listingFields.filter(
    item => item.key == 'outdoor_spaces'
  );
  const indoorSpaces = listingConfig.listingFields.filter(
    item => item.key == 'indoor_spaces'
  );
  const safetyAmenities = listingConfig.listingFields.filter(
    item => item.key == 'safetyAmenities'
  );
  const practicalAmenities = listingConfig.listingFields.filter(
    item => item.key == 'practical_amenities'
  );
  const standoutAmenities = listingConfig.listingFields.filter(
    item => item.key == 'standoutAmenities'
  );
  const ecoFriendlyAmenities = listingConfig.listingFields.filter(
    item => item.key == 'ecoFriendlyAmenities'
  );
  const familyAmenities = listingConfig.listingFields.filter(
    item => item.key == 'familyAmenities'
  );
  const homeCareRequests = listingConfig.listingFields.filter(
    item => item.key == 'homeCareRequests'
  );
  const homePreferences = listingConfig.listingFields.filter(
    item => item.key == 'homePreferences'
  );
  const family_necessities = listingConfig.listingFields.filter(
    item => item.key == 'family_necessities'
  );
  const medical_necessities = listingConfig.listingFields.filter(
    item => item.key == 'medical_necessities'
  );

  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant =
    rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));
  const currentListingId = currentListing.id?.uuid;
  const currentFavorites =
    currentUser?.attributes?.profile?.publicData?.favorites || [];
  const [favoriteIds, setFavoriteIds] = useState(
    currentFavorites.map(normalizeFavoriteId)
  );

  useEffect(() => {
    let rafId = null;

    const computeMax = () => {
      const wrapper = bedroomWrapperRef.current;
      const track = wrapper?.querySelector('[data-bedroom-track]');
      if (wrapper && track) {
        // Read both layout properties together, then commit to state.
        const maxOffset = Math.max(0, track.scrollWidth - wrapper.clientWidth);
        setBedroomMaxOffset(maxOffset);
      }
    };

    // Coalesce resize bursts into one layout read per frame to avoid forcing a
    // synchronous reflow on every resize event.
    const scheduleComputeMax = () => {
      if (rafId == null) {
        rafId = window.requestAnimationFrame(() => {
          rafId = null;
          computeMax();
        });
      }
    };

    computeMax();
    window.addEventListener('resize', scheduleComputeMax);
    return () => {
      window.removeEventListener('resize', scheduleComputeMax);
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [currentListingId]);

  useEffect(() => {
    const updatedFavorites =
      currentUser?.attributes?.profile?.publicData?.favorites || [];
    setFavoriteIds(updatedFavorites.map(normalizeFavoriteId));
  }, [currentUser]);

  useEffect(() => {
    if (!photoModalOpen || typeof window === 'undefined') {
      return undefined;
    }

    let rafId = null;

    const updateTopOffset = () => {
      const header = document.querySelector('header');
      setPhotoModalTopOffset(header ? header.getBoundingClientRect().bottom : 0);
    };

    // Coalesce resize bursts so getBoundingClientRect (a layout read) runs at
    // most once per animation frame instead of synchronously per resize event.
    const scheduleUpdateTopOffset = () => {
      if (rafId == null) {
        rafId = window.requestAnimationFrame(() => {
          rafId = null;
          updateTopOffset();
        });
      }
    };

    updateTopOffset();
    window.addEventListener('resize', scheduleUpdateTopOffset);
    return () => {
      window.removeEventListener('resize', scheduleUpdateTopOffset);
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [photoModalOpen]);

  const listingFavoriteId = normalizeFavoriteId(currentListingId);
  const isListingFavorite = listingFavoriteId
    ? favoriteIds.includes(listingFavoriteId)
    : false;

  const handleToggleFavorite = e => {
    e.preventDefault();
    e.stopPropagation();

    if (!listingFavoriteId) {
      return;
    }

    if (!isAuthenticated) {
      window.location.href = '/login';
      return;
    }

    const savedFavorites =
      currentUser?.attributes?.profile?.publicData?.favorites ||
      favoriteIds ||
      [];
    const savedFavoriteIds = savedFavorites.map(normalizeFavoriteId);
    const alreadyFavorite = savedFavoriteIds.includes(listingFavoriteId);
    const updatedFavorites = alreadyFavorite
      ? savedFavoriteIds.filter(favoriteId => favoriteId !== listingFavoriteId)
      : [...savedFavoriteIds, listingFavoriteId];

    setFavoriteIds(updatedFavorites);

    onToggleFavorite({ publicData: { favorites: updatedFavorites } }).catch(
      () => {
        setFavoriteIds(savedFavoriteIds);
      }
    );
  };

  const listingSlug =
    rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id &&
    currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage =
    pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return (
      <NamedRedirect
        name="ListingPage"
        params={params}
        search={location.search}
      />
    );
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return (
      <ErrorPage
        topbar={topbar}
        scrollingDisabled={scrollingDisabled}
        intl={intl}
      />
    );
  } else if (!currentListing.id) {
    // Still loading the listing
    return (
      <LoadingPage
        topbar={topbar}
        scrollingDisabled={scrollingDisabled}
        intl={intl}
      />
    );
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const bedTypeDetails = getBedTypeDetails(publicData);

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable &&
    currentListing.author.id.uuid === currentUser.id.uuid;
  const {
    listingType,
    transactionProcessAlias,
    unitType,
    homeSize,
    homeSizeUnit,
    homeType,
    NumberofBathrooms,
    NumberofBedrooms,
    maximumGuests,
    transportCarsCounts,
    homeCareRequestspetCare,
    homePrefrenceRequestsOther,
    homeCareRequestsOther,
    familyActivityNotes,
    PracticalAmenitiesOther,
    hospitalName,
    hosptalDistance,
    outdoorSpacesOther,
    indoorSpacesOther,
    costValues,
    carDetails,
    hometypeOther,
    PracticalAmenities,
    ecoFriendlyAmenitiesOther,
    familyAmenitiesOther,
    safetyAmenitiesOther,
    standoutAmenitiesOther,
    transportationAvailable,
    paymentPreferences,
    paymentModeOther,
  } = publicData;
  const getAvailableTransportation = () => {
    if (!transportationAvailable) return [];
    return transportationAvailable
      .filter(item => item.count > 0)
      .map((item, index) => (
        <div key={index} className={css.checkItemContainer}>
          <div className={css.iconNname}>
            <div>
              {item.name === 'Car' && carDetails ? (
                <div className={css.carDetails}>
                  {Array.isArray(carDetails) &&
                    carDetails
                      .filter(car => {
                        if (!car) return false;
                        const makeModel =
                          typeof car.makeModel === 'string'
                            ? car.makeModel.trim()
                            : car.makeModel;
                        const seats =
                          typeof car.seats === 'string'
                            ? car.seats.trim()
                            : car.seats;
                        const transmission =
                          typeof car.transmission === 'string'
                            ? car.transmission.trim()
                            : car.transmission;
                        return Boolean(makeModel || seats || transmission);
                      })
                      .map((car, carIndex) => (
                        <div key={carIndex} className={css.carItem}>
                          <div className={css.iconNname}>
                            <div className={css.iconWrapper}>
                              <svg
                                width="9"
                                height="9"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  className={css.marketplaceFill}
                                  d="M2.636621 7.7824771L.3573694 5.6447948c-.4764924-.4739011-.4764924-1.2418639 0-1.7181952.4777142-.473901 1.251098-.473901 1.7288122 0l1.260291 1.1254783L6.1721653.505847C6.565577-.0373166 7.326743-.1636902 7.8777637.227582c.5473554.3912721.6731983 1.150729.2797866 1.6951076L4.4924979 7.631801c-.2199195.306213-.5803433.5067096-.9920816.5067096-.3225487 0-.6328797-.1263736-.8637952-.3560334z"
                                  fillRule="evenodd"
                                />
                              </svg>
                            </div>
                            <div>
                              {car.makeModel} ({car.transmission}) – {car.seats}{' '}
                              seats
                            </div>
                          </div>
                        </div>
                      ))}
                </div>
              ) : (
                <div>
                  <div className={css.iconNname}>
                    <div className={css.iconWrapper}>
                      <svg
                        width="9"
                        height="9"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          className={css.marketplaceFill}
                          d="M2.636621 7.7824771L.3573694 5.6447948c-.4764924-.4739011-.4764924-1.2418639 0-1.7181952.4777142-.473901 1.251098-.473901 1.7288122 0l1.260291 1.1254783L6.1721653.505847C6.565577-.0373166 7.326743-.1636902 7.8777637.227582c.5473554.3912721.6731983 1.150729.2797866 1.6951076L4.4924979 7.631801c-.2199195.306213-.5803433.5067096-.9920816.5067096-.3225487 0-.6328797-.1263736-.8637952-.3560334z"
                          fillRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div>{item.name}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={css.labelWrapper}>
            {item.name !== 'Car' && `-`}{' '}
            {item.name !== 'Car' && `${item.count}`}
          </div>
        </div>
      ));
  };
  const ageOfChild = () => {
    const selectedAges = publicData?.ageSuitability || [];
    return selectedAges
      .map(selectedValue => {
        const matchingAge = ageSuitability.find(
          age => age.value === selectedValue
        );
        return matchingAge ? matchingAge.label : selectedValue;
      })
      .join(', ');
  };
  // Format payment preferences array (e.g. ["venmo","wise"] -> "Venmo, Wise")
  // If an item is 'other', include paymentModeOther in parentheses: "Other (cash)"
  const formatPaymentPreferences = (prefs, paymentModeOther) => {
    if (!prefs) return '';
    if (Array.isArray(prefs)) {
      return prefs
        .filter(Boolean)
        .map(p => {
          const s = String(p).toLowerCase();
          if (s === 'other') {
            const otherText = paymentModeOther
              ? String(paymentModeOther).trim()
              : '';
            return otherText ? `Other (${otherText})` : 'Other';
          }
          return s.charAt(0).toUpperCase() + s.slice(1);
        })
        .join(', ');
    }
    // prefs is a single value
    const s = String(prefs).toLowerCase();
    if (s === 'other') {
      return paymentModeOther
        ? `Other (${String(paymentModeOther).trim()})`
        : 'Other';
    }
    return s.charAt(0).toUpperCase() + s.slice(1);
  };
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage
        topbar={topbar}
        scrollingDisabled={scrollingDisabled}
        intl={intl}
        invalidListing
      />
    );
  }
  const processName = resolveLatestProcessName(
    transactionProcessAlias.split('/')[0]
  );
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking
    ? 'booking'
    : isPurchase
      ? 'purchase'
      : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing &&
    processType !== 'inquiry' &&
    !currentUser?.attributes?.stripeConnected;
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage
        id="ListingPage.payoutDetailsWarning"
        values={{ processType }}
      />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');
  const hostFamilyLabel = getHostFamilyLabel(ensuredAuthor, authorDisplayName);
  const authorBio = trimText(ensuredAuthor?.attributes?.profile?.bio);

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
    onTransition,
    onSendMessage,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed =
      currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const schemaPriceMaybe = price
    ? {
      price: intl.formatNumber(convertMoneyToNumber(price), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      priceCurrency: price.currency,
    }
    : {};
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability
    ? { availability: schemaAvailability }
    : {};
  const user = currentListing.author;
  const userIsCurrentUser = user && user.type === 'currentUser';
  const ensuredUser = userIsCurrentUser
    ? ensureCurrentUser(user)
    : ensureUser(user);

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const isCurrentUser =
    ensuredUser.id &&
    ensuredCurrentUser.id &&
    ensuredUser.id.uuid === ensuredCurrentUser.id.uuid;

  const handleCheckProfileModalClose = () => setCheckSubscription(false);
  const listingTitle = title || 'Untitled listing';
  const locationLabel = getLocationLabel(publicData?.location?.address);
  const locationText = locationLabel;
  const introHomeTypeLabel =
    homeType && homeType !== 'other'
      ? formatLabel(homeType)
      : hometypeOther || formatLabel(listingType);
  const introHeading =
    locationLabel && locationLabel !== 'Location not added'
      ? `${introHomeTypeLabel !== emptyValue ? introHomeTypeLabel : 'Home'} in ${locationLabel}`
      : listingTitle;
  const galleryImageItems = getGalleryImageItems(
    publicData,
    currentListing.images,
    config.layout.listingImage.variantPrefix
  );
  const galleryImages = galleryImageItems.map(image => image.url);
  const photoCount = galleryImages.length;
  const currentPhotoItem =
    galleryImageItems[activePhotoIndex] || galleryImageItems[0];
  const currentPhoto = currentPhotoItem?.url;
  const currentPhotoDescription = currentPhotoItem?.description;
  const bedrooms = getBedroomCards(publicData);
  const amenityGroups = getAmenityGroups(publicData);
  const amenityGroupsWithIcons = amenityGroups.map(group => ({
    ...group,
    items: group.items.map(item => ({
      ...item,
      icon: getTaxanomyIconForDisplay(taxanomyIconOptions, item),
    })),
  }));
  const amenityItems = sortItemsAlphabetically(
    amenityGroupsWithIcons.flatMap(group => group.items)
  );
  const visibleAmenities = amenityItems.slice(0, visibleAmenityCount);
  const hasMoreAmenities = amenityItems.length > 1;
  const surroundingAdditionalInfo =
    publicData?.surroundingArea?.additionalInfo || {};
  const surroundingGroups = getSurroundingGroups(
    publicData,
    taxanomyIconOptions
  ).filter(
    group => group.items.length || trimText(surroundingAdditionalInfo[group.id])
  );
  const nearbyPreviewGroups = nearbyPreviewGroupOrder
    .map(groupId => surroundingGroups.find(group => group.id === groupId))
    .filter(Boolean);
  const preferencesAdditionalInfo =
    publicData?.preferencesAndCare?.additionalInfo || {};
  const preferenceGroupsWithIcons = getPreferenceGroups(publicData).map(group => ({
    ...group,
    items: withTaxanomyIcons(group.items, taxanomyIconOptions),
    note: trimText(preferencesAdditionalInfo[group.id]),
  }));
  const preferenceItems = sortItemsAlphabetically(
    preferenceGroupsWithIcons.flatMap(group => group.items)
  );
  const safetyItems = sortItemsAlphabetically(
    withTaxanomyIcons(getSafetyAmenityItems(publicData), taxanomyIconOptions)
  );
  const healthItems = sortItemsAlphabetically(
    surroundingGroups.find(group => group.id === 'health-and-medical')?.items ||
    []
  );
  const paymentTypes = getPaymentTypeLabels(publicData);
  const cancellationPolicy = getCancellationPolicy(
    publicData?.cancellationPolicy
  );
  const feeRows = getFeeRows(publicData?.fees, price);
  const monthlyCost = getMonthlyCostRows(publicData);
  const monthlyTotal =
    monthlyCost.total > 0
      ? formatCurrencyValue(monthlyCost.total)
      : formattedPrice;
  const listingStats = getListingStats(publicData);
  const homeTypeLabel = introHomeTypeLabel;
  const subtitle = `${homeTypeLabel !== emptyValue ? homeTypeLabel : 'Home'
    } • Owned by ${authorDisplayName || 'Host'}`;
  const descriptionText =
    description || 'No listing description has been added yet.';
  const shouldTruncateDescription =
    descriptionText.length > descriptionPreviewLength;
  const visibleDescriptionText =
    shouldTruncateDescription && !expandedSections.description
      ? `${dropDanglingTokens(
        descriptionText.slice(0, descriptionPreviewLength)
      ).trim()}...`
      : descriptionText;
  const mapProps = getMapProps(
    geolocation,
    publicData,
    config.maps,
    currentListing.id
  );
  const googleMapsUrl = geolocation
    ? `https://maps.google.com/?q=${geolocation.lat},${geolocation.lng}`
    : publicData?.location?.address
      ? `https://maps.google.com/?q=${encodeURIComponent(
        publicData.location.address
      )}`
      : null;

  const openPhotoModal = index => {
    if (photoCount > 0) {
      setActivePhotoIndex(index);
      setPhotoModalOpen(true);
    }
  };

  const showPreviousPhoto = () => {
    setActivePhotoIndex(prevIndex =>
      photoCount > 0 ? (prevIndex - 1 + photoCount) % photoCount : 0
    );
  };

  const showNextPhoto = () => {
    setActivePhotoIndex(prevIndex =>
      photoCount > 0 ? (prevIndex + 1) % photoCount : 0
    );
  };

  const scrollBedrooms = direction => {
    const wrapper = bedroomWrapperRef.current;
    if (!wrapper) return;
    const cardWidth = wrapper.querySelector('article')?.offsetWidth || 0;
    const gap = 14;
    const step = cardWidth + gap;
    if (direction === 'next') {
      setBedroomOffset(prev => Math.min(bedroomMaxOffset, prev + step));
    } else {
      setBedroomOffset(prev => Math.max(0, prev - step));
    }
  };

  const openDetailModal = nextModal => {
    setDetailModal(nextModal);
  };

  const closeDetailModal = () => {
    setDetailModal(null);
  };

  const openAmenitiesModal = () => {
    openDetailModal({
      type: 'amenities',
      title: 'What this home offers',
      sections: amenityGroupsWithIcons
        .filter(group => group.items.length)
        .map(group => ({
          id: group.id,
          title: group.name,
          items: group.items,
        })),
    });
  };

  const openPolicyModal = () => {
    if (cancellationPolicy) {
      openDetailModal({
        type: 'policy',
        title: 'Cancellation policy',
        policy: cancellationPolicy,
      });
    }
  };

  const renderLocationGroup = group => {
    const previewCount = nearbyPreviewCounts[group.id] || 5;
    const sortedItems = sortItemsAlphabetically(group.items);
    const visibleItems = sortedItems.slice(0, previewCount);
    const note = trimText(surroundingAdditionalInfo[group.id]);
    const showDetailsButton = sortedItems.length > 1 || !!note;

    return (
      <div key={group.id} className={css.nearbyColumn}>
        <h3 className={css.nearbyColumnTitle}>{group.name}</h3>
        {visibleItems.map(item => (
          <div key={item.id} className={css.nearbyItem}>
            <NearbyIcon icon={item.icon} isCustom={item.isCustom} />
            <span className={css.nearbyItemName}>
              {sentenceCase(item.name)}
              {item.distance ? (
                <span className={css.nearbyInlineDistance}>
                  {' '}
                  • {item.distance}
                </span>
              ) : null}
            </span>
          </div>
        ))}
        {showDetailsButton ? (
          <button
            className={css.newTextButton}
            type="button"
            onClick={() =>
              openDetailModal({
                type: 'nearby',
                title: group.name,
                items: sortedItems,
                note,
              })
            }
          >
            {sortedItems.length > 1 ? 'See more' : 'Show details'}
          </button>
        ) : null}
      </div>
    );
  };

  const renderHighlightCard = (cardTitle, items, sectionKey, options = {}) => {
    const previewCount = visibleHighlightCounts[sectionKey] || 5;
    const sortedItems = sortItemsAlphabetically(items);
    const visibleItems = sortedItems.slice(0, previewCount);
    const note = trimText(options.note);
    const modalSections = options.sections;
    const hasSectionNotes =
      Array.isArray(modalSections) &&
      modalSections.some(section => trimText(section.note));
    const showDetailsButton =
      sortedItems.length > 1 || !!note || hasSectionNotes;

    return (
      <article className={css.highlightCard}>
        <div className={css.highlightCardHeading}>
          <SectionGlyph type={options.icon} />
          <h3>{cardTitle}</h3>
        </div>
        {visibleItems.length ? (
          <ul className={css.highlightList}>
            {visibleItems.map(item => (
              <li key={`${sectionKey}-${item.id}`}>
                {sentenceCase(item.name)}
                {options.showDistance && item.distance ? (
                  <span> • {item.distance}</span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className={css.highlightEmpty}>No details added yet.</p>
        )}
        {showDetailsButton ? (
          <button
            className={css.newTextButton}
            type="button"
            onClick={() =>
              openDetailModal({
                type: sectionKey === 'health' ? 'nearby' : 'detail',
                title: cardTitle,
                ...(modalSections
                  ? { sections: modalSections }
                  : { items: sortedItems }),
                note,
              })
            }
          >
            <FormattedMessage id="ListingPage.detailsButton" />
          </button>
        ) : null}
      </article>
    );
  };

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...schemaPriceMaybe,
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={topbar}
        footer={<FooterContainer />}
      >
        <div className={css.newListingPage}>
          <main className={css.newListingContainer}>
            <div className={css.newTitleRow}>
              <h1>{listingTitle}</h1>
              <div className={css.newTitleActions}>
                {currentListing.id ? (
                  <ActionBarMaybe
                    className={css.newActionBar}
                    isOwnListing={isOwnListing}
                    listing={currentListing}
                    editParams={{
                      id: listingId.uuid,
                      slug: listingSlug,
                      type: listingPathParamType,
                      tab: listingTab,
                    }}
                  />
                ) : null}
                {!isOwnListing ? (
                  <button
                    className={css.newSavedLabel}
                    type="button"
                    onClick={handleToggleFavorite}
                    aria-pressed={isListingFavorite}
                  >
                    <span>{isListingFavorite ? 'Saved!' : 'Save'}</span>
                    <HeartIcon isActive={isListingFavorite} />
                  </button>
                ) : null}
              </div>
            </div>

            <section className={css.newGalleryGrid}>
              <GalleryImage
                src={galleryImages[0]}
                title={listingTitle}
                className={css.newHeroImage}
                priority
              />
              <GalleryImage src={galleryImages[1]} title={listingTitle} />
              <GalleryImage
                src={galleryImages[2]}
                title={listingTitle}
                className={css.newTopRightImage}
              />
              <GalleryImage src={galleryImages[3]} title={listingTitle} />
              <GalleryImage
                src={galleryImages[4]}
                title={listingTitle}
                className={css.newBottomRightImage}
              />
              {photoCount > 0 ? (
                <button
                  className={css.newPhotoButton}
                  type="button"
                  onClick={() => openPhotoModal(0)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M5.5 16.5C6.03043 16.5 6.53914 16.7107 6.91421 17.0858C7.28929 17.4609 7.5 17.9696 7.5 18.5C7.5 19.0304 7.28929 19.5391 6.91421 19.9142C6.53914 20.2893 6.03043 20.5 5.5 20.5C4.96957 20.5 4.46086 20.2893 4.08579 19.9142C3.71071 19.5391 3.5 19.0304 3.5 18.5C3.5 17.9696 3.71071 17.4609 4.08579 17.0858C4.46086 16.7107 4.96957 16.5 5.5 16.5ZM12 16.5C12.5304 16.5 13.0391 16.7107 13.4142 17.0858C13.7893 17.4609 14 17.9696 14 18.5C14 19.0304 13.7893 19.5391 13.4142 19.9142C13.0391 20.2893 12.5304 20.5 12 20.5C11.4696 20.5 10.9609 20.2893 10.5858 19.9142C10.2107 19.5391 10 19.0304 10 18.5C10 17.9696 10.2107 17.4609 10.5858 17.0858C10.9609 16.7107 11.4696 16.5 12 16.5ZM18.5 16.5C19.0304 16.5 19.5391 16.7107 19.9142 17.0858C20.2893 17.4609 20.5 17.9696 20.5 18.5C20.5 19.0304 20.2893 19.5391 19.9142 19.9142C19.5391 20.2893 19.0304 20.5 18.5 20.5C17.9696 20.5 17.4609 20.2893 17.0858 19.9142C16.7107 19.5391 16.5 19.0304 16.5 18.5C16.5 17.9696 16.7107 17.4609 17.0858 17.0858C17.4609 16.7107 17.9696 16.5 18.5 16.5ZM5.5 10C6.03043 10 6.53914 10.2107 6.91421 10.5858C7.28929 10.9609 7.5 11.4696 7.5 12C7.5 12.5304 7.28929 13.0391 6.91421 13.4142C6.53914 13.7893 6.03043 14 5.5 14C4.96957 14 4.46086 13.7893 4.08579 13.4142C3.71071 13.0391 3.5 12.5304 3.5 12C3.5 11.4696 3.71071 10.9609 4.08579 10.5858C4.46086 10.2107 4.96957 10 5.5 10ZM12 10C12.5304 10 13.0391 10.2107 13.4142 10.5858C13.7893 10.9609 14 11.4696 14 12C14 12.5304 13.7893 13.0391 13.4142 13.4142C13.0391 13.7893 12.5304 14 12 14C11.4696 14 10.9609 13.7893 10.5858 13.4142C10.2107 13.0391 10 12.5304 10 12C10 11.4696 10.2107 10.9609 10.5858 10.5858C10.9609 10.2107 11.4696 10 12 10ZM18.5 10C19.0304 10 19.5391 10.2107 19.9142 10.5858C20.2893 10.9609 20.5 11.4696 20.5 12C20.5 12.5304 20.2893 13.0391 19.9142 13.4142C19.5391 13.7893 19.0304 14 18.5 14C17.9696 14 17.4609 13.7893 17.0858 13.4142C16.7107 13.0391 16.5 12.5304 16.5 12C16.5 11.4696 16.7107 10.9609 17.0858 10.5858C17.4609 10.2107 17.9696 10 18.5 10ZM5.5 3.5C6.03043 3.5 6.53914 3.71071 6.91421 4.08579C7.28929 4.46086 7.5 4.96957 7.5 5.5C7.5 6.03043 7.28929 6.53914 6.91421 6.91421C6.53914 7.28929 6.03043 7.5 5.5 7.5C4.96957 7.5 4.46086 7.28929 4.08579 6.91421C3.71071 6.53914 3.5 6.03043 3.5 5.5C3.5 4.96957 3.71071 4.46086 4.08579 4.08579C4.46086 3.71071 4.96957 3.5 5.5 3.5ZM12 3.5C12.5304 3.5 13.0391 3.71071 13.4142 4.08579C13.7893 4.46086 14 4.96957 14 5.5C14 6.03043 13.7893 6.53914 13.4142 6.91421C13.0391 7.28929 12.5304 7.5 12 7.5C11.4696 7.5 10.9609 7.28929 10.5858 6.91421C10.2107 6.53914 10 6.03043 10 5.5C10 4.96957 10.2107 4.46086 10.5858 4.08579C10.9609 3.71071 11.4696 3.5 12 3.5ZM18.5 3.5C19.0304 3.5 19.5391 3.71071 19.9142 4.08579C20.2893 4.46086 20.5 4.96957 20.5 5.5C20.5 6.03043 20.2893 6.53914 19.9142 6.91421C19.5391 7.28929 19.0304 7.5 18.5 7.5C17.9696 7.5 17.4609 7.28929 17.0858 6.91421C16.7107 6.53914 16.5 6.03043 16.5 5.5C16.5 4.96957 16.7107 4.46086 17.0858 4.08579C17.4609 3.71071 17.9696 3.5 18.5 3.5Z"
                      fill="black"
                    />
                  </svg>
                  View {photoCount} {photoCount === 1 ? 'Photo' : 'Photos'}
                </button>
              ) : null}
            </section>

            <Modal
              id="ListingPage.photoGallery"
              scrollLayerClassName={css.photoModalScrollLayer}
              containerClassName={css.photoModalContainer}
              isOpen={photoModalOpen}
              onClose={() => setPhotoModalOpen(false)}
              usePortal
              onManageDisableScrolling={onManageDisableScrolling}
            >
              <div className={css.photoModalContent}>
                <div className={css.photoCounter}>
                  {photoCount ? `${activePhotoIndex + 1}/${photoCount}` : null}
                </div>
                <button
                  className={css.photoCloseButton}
                  type="button"
                  onClick={() => setPhotoModalOpen(false)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="m6 6 12 12M18 6 6 18"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  Close
                </button>
                <div className={css.photoImageWrapper}>
                  {currentPhoto ? (
                    <img
                      className={css.photoModalImage}
                      src={currentPhoto}
                      alt={`${listingTitle} photo ${activePhotoIndex + 1}`}
                    />
                  ) : null}
                  {currentPhotoDescription ? (
                    <div className={css.photoCaption}>
                      <div className={css.photoCaptionText}>
                        {renderDescription(currentPhotoDescription)}
                      </div>
                    </div>
                  ) : null}
                </div>

                {photoCount > 1 ? (
                  <button
                    className={classNames(css.photoNavButton, css.photoNavPrev)}
                    type="button"
                    onClick={showPreviousPhoto}
                    aria-label="Previous photo"
                  >
                    <svg width="14" height="24" viewBox="0 0 18 30" aria-hidden="true">
                      <path
                        d="M16 2 3 15l13 13"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                ) : null}
                {photoCount > 1 ? (
                  <button
                    className={classNames(css.photoNavButton, css.photoNavNext)}
                    type="button"
                    onClick={showNextPhoto}
                    aria-label="Next photo"
                  >
                    <svg width="14" height="24" viewBox="0 0 18 30" aria-hidden="true">
                      <path
                        d="m2 2 13 13L2 28"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                ) : null}
              </div>
            </Modal>

            <div className={css.newContentGrid}>
              <div className={css.newMainColumn}>
                <section className={css.newIntroBlock}>
                  <h2>{introHeading}</h2>
                  {/* <p className={css.newSubtitle}>{subtitle}</p> */}
                  {listingStats.length ? (
                    <p className={css.newStatLine}>
                      {listingStats.join(' • ')}
                    </p>
                  ) : null}
                </section>

                <section className={css.newHostBlock}>
                  <div className={css.newHostAvatar}>
                    <AvatarMedium
                      rootClassName={css.newHostAvatarImage}
                      initialsClassName={css.newHostAvatarInitials}
                      user={ensuredAuthor}
                      disableProfileLink
                    />
                  </div>
                  <div>
                    <h2>
                      Hosted by the <b>{hostFamilyLabel}</b> family
                    </h2>
                    {authorBio ? <p>{authorBio}</p> : null}
                    <div className={css.hostCtas}>
                      {ensuredAuthor.id ? (
                        <NamedLink
                          className={css.newTextButton}
                          name="ProfilePage"
                          params={{ id: ensuredAuthor.id.uuid }}
                        >
                          View profile
                        </NamedLink>
                      ) : null}
                      {ensuredAuthor.id && !isCurrentUser ? (
                        <span className={css.hostCtaSeparator}>•</span>
                      ) : null}
                      {!isCurrentUser ? (
                        <button
                          className={css.newTextButton}
                          type="button"
                          onClick={onContactUser}
                        >
                          Message
                        </button>
                      ) : null}
                    </div>
                  </div>
                </section>

                <section className={css.newCopyBlock}>
                  <div className={css.newDescriptionText}>
                    {renderDescription(visibleDescriptionText)}
                  </div>
                  {shouldTruncateDescription &&
                    !expandedSections.description ? (
                    <button
                      className={css.newTextButton}
                      type="button"
                      aria-expanded="false"
                      onClick={() => showMore('description')}
                    >
                      Show more
                    </button>
                  ) : shouldTruncateDescription ? (
                    <button
                      className={css.newTextButton}
                      type="button"
                      aria-expanded="true"
                      onClick={() => showLess('description')}
                    >
                      Show less
                    </button>
                  ) : null}
                </section>

                <section className={css.newSection}>
                  <div className={css.sectionHeaderWithAction}>
                    <h2>Where you’ll sleep</h2>
                  </div>
                  {bedrooms.length ? (
                    <div className={css.bedroomCarouselWrapper}>
                      <div
                        className={css.bedroomTrackWrapper}
                        ref={bedroomWrapperRef}
                      >
                        <div
                          className={css.bedroomTrack}
                          data-bedroom-track="true"
                          style={{
                            transform: `translateX(-${bedroomOffset}px)`,
                          }}
                        >
                          {bedrooms.map(room => (
                            <article
                              key={room.title}
                              className={css.bedroomCard}
                            >
                              <div className={css.bedIconRow}>
                                {(room.bedIconTypes.length
                                  ? room.bedIconTypes
                                  : ['king']
                                ).map((bedType, index) => (
                                  <BedIcon
                                    key={`${room.title}-${bedType}-${index}`}
                                    bedType={bedType}
                                  />
                                ))}
                              </div>
                              <h3>{room.title}</h3>
                              <p>{room.summary}</p>
                            </article>
                          ))}
                        </div>
                      </div>
                      {bedrooms.length > 1 ? (
                        <>
                          {bedroomOffset > 0 ? (
                            <button
                              type="button"
                              className={classNames(
                                css.bedroomCarouselBtn,
                                css.bedroomCarouselBtnPrev
                              )}
                              onClick={() => scrollBedrooms('prev')}
                              aria-label="Previous bedrooms"
                            >
                              <svg
                                width="44"
                                height="44"
                                viewBox="0 0 44 44"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g filter="url(#lp_filter_prev)">
                                  <circle
                                    cx="22"
                                    cy="18"
                                    r="18"
                                    fill="#712739"
                                  />
                                </g>
                                <path
                                  d="M24.7854 24.0712L17.3926 17.0891L24.7854 10.1069"
                                  stroke="white"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <defs>
                                  <filter
                                    id="lp_filter_prev"
                                    x="0"
                                    y="0"
                                    width="44"
                                    height="44"
                                    filterUnits="userSpaceOnUse"
                                    colorInterpolationFilters="sRGB"
                                  >
                                    <feFlood
                                      floodOpacity="0"
                                      result="BackgroundImageFix"
                                    />
                                    <feColorMatrix
                                      in="SourceAlpha"
                                      type="matrix"
                                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                      result="hardAlpha"
                                    />
                                    <feOffset dy="4" />
                                    <feGaussianBlur stdDeviation="2" />
                                    <feComposite
                                      in2="hardAlpha"
                                      operator="out"
                                    />
                                    <feColorMatrix
                                      type="matrix"
                                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                                    />
                                    <feBlend
                                      mode="normal"
                                      in2="BackgroundImageFix"
                                      result="effect1_dropShadow"
                                    />
                                    <feBlend
                                      mode="normal"
                                      in="SourceGraphic"
                                      in2="effect1_dropShadow"
                                      result="shape"
                                    />
                                  </filter>
                                </defs>
                              </svg>
                            </button>
                          ) : null}
                          {bedroomOffset < bedroomMaxOffset ? (
                            <button
                              type="button"
                              className={classNames(
                                css.bedroomCarouselBtn,
                                css.bedroomCarouselBtnNext
                              )}
                              onClick={() => scrollBedrooms('next')}
                              aria-label="Next bedrooms"
                            >
                              <svg
                                width="44"
                                height="44"
                                viewBox="0 0 44 44"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g filter="url(#lp_filter_next)">
                                  <circle
                                    cx="22"
                                    cy="18"
                                    r="18"
                                    fill="#712739"
                                  />
                                </g>
                                <path
                                  d="M19.2146 24.0712L26.6074 17.0891L19.2146 10.1069"
                                  stroke="white"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <defs>
                                  <filter
                                    id="lp_filter_next"
                                    x="0"
                                    y="0"
                                    width="44"
                                    height="44"
                                    filterUnits="userSpaceOnUse"
                                    colorInterpolationFilters="sRGB"
                                  >
                                    <feFlood
                                      floodOpacity="0"
                                      result="BackgroundImageFix"
                                    />
                                    <feColorMatrix
                                      in="SourceAlpha"
                                      type="matrix"
                                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                      result="hardAlpha"
                                    />
                                    <feOffset dy="4" />
                                    <feGaussianBlur stdDeviation="2" />
                                    <feComposite
                                      in2="hardAlpha"
                                      operator="out"
                                    />
                                    <feColorMatrix
                                      type="matrix"
                                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                                    />
                                    <feBlend
                                      mode="normal"
                                      in2="BackgroundImageFix"
                                      result="effect1_dropShadow"
                                    />
                                    <feBlend
                                      mode="normal"
                                      in="SourceGraphic"
                                      in2="effect1_dropShadow"
                                      result="shape"
                                    />
                                  </filter>
                                </defs>
                              </svg>
                            </button>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  ) : (
                    <p className={css.newEmptyText}>
                      No bedroom details added yet.
                    </p>
                  )}
                </section>

                <section className={css.newSection}>
                  <h2>What this home offers</h2>
                  <div className={css.newAmenityGrid}>
                    {visibleAmenities.length ? (
                      visibleAmenities.map(item => (
                        <div
                          key={`${item.categoryId}-${item.id}`}
                          className={css.newAmenityItem}
                        >
                          <AmenityIcon
                            icon={item.icon}
                            isCustom={item.isCustom}
                          />
                          <span>{sentenceCase(item.name)}</span>
                        </div>
                      ))
                    ) : (
                      <p className={css.newEmptyText}>
                        No amenities selected yet.
                      </p>
                    )}
                  </div>
                  {hasMoreAmenities ? (
                    <button
                      className={css.newTextButton}
                      type="button"
                      onClick={openAmenitiesModal}
                    >
                      See more
                    </button>
                  ) : null}
                </section>
              </div>

              <aside className={css.newBookingPanel}>
                <OrderPanel
                  className={css.productOrderPanel}
                  panelVariant="gfhr"
                  listing={currentListing}
                  isOwnListing={isOwnListing}
                  onSubmit={handleOrderSubmit}
                  handleCLick={handleCLick}
                  authorLink={
                    <NamedLink
                      className={css.authorNameLink}
                      name="ProfilePage"
                      params={{ id: ensuredAuthor.id?.uuid || params.id }}
                    >
                      {authorDisplayName}
                    </NamedLink>
                  }
                  title={
                    <FormattedMessage
                      id="ListingPage.orderTitle"
                      values={{ title: richTitle }}
                    />
                  }
                  titleDesktop={
                    <H4 as="h1" className={css.orderPanelTitle}>
                      Listing details
                    </H4>
                  }
                  payoutDetailsWarning={payoutDetailsWarning}
                  author={ensuredAuthor}
                  onManageDisableScrolling={onManageDisableScrolling}
                  onContactUser={onContactUser}
                  monthlyTimeSlots={monthlyTimeSlots}
                  onFetchTimeSlots={onFetchTimeSlots}
                  onFetchTransactionLineItems={onFetchTransactionLineItems}
                  lineItems={lineItems}
                  fetchLineItemsInProgress={fetchLineItemsInProgress}
                  fetchLineItemsError={fetchLineItemsError}
                  validListingTypes={config.listing.listingTypes}
                  marketplaceCurrency={config.currency}
                  dayCountAvailableForBooking={
                    config.stripe.dayCountAvailableForBooking
                  }
                  marketplaceName={config.marketplaceName}
                />
              </aside>

              <section
                className={classNames(css.newSection, css.fullWidthSection)}
              >
                <div className={css.sectionHeaderRow}>
                  <div>
                    <h2>Where you'll be</h2>
                    <p className={css.locationText}>{locationText}</p>
                  </div>
                  {/* {googleMapsUrl ? (
                    <ExternalLink className={css.mapLink} href={googleMapsUrl}>
                      View on Google Maps
                    </ExternalLink>
                  ) : null} */}
                </div>
                {mapProps ? (
                  <div className={css.newMapPreview}>
                    {/* Interactive map so the + / − zoom controls render and the
                        5 km privacy-radius zoom cap applies. Shown on both
                        desktop and mobile. */}
                    <Map {...mapProps} mapsConfig={config.maps} />
                  </div>
                ) : (
                  <div className={css.newMapPreviewEmpty}>{locationLabel}</div>
                )}
                {nearbyPreviewGroups.length ? (
                  <div className={css.nearbyGrid}>
                    {nearbyPreviewGroups.map(renderLocationGroup)}
                  </div>
                ) : null}
              </section>

              <section
                className={classNames(css.newSection, css.fullWidthSection)}
              >
                <h2>What you’ll pay monthly</h2>
                <div className={css.pricingGrid}>
                  <div className={css.costTable}>
                    {monthlyCost.definedRows.map(row => (
                      <div key={row.label} className={css.costRow}>
                        <span>{row.label}</span>
                        <strong>{formatCurrencyValue(row.numericValue)}</strong>
                      </div>
                    ))}
                    {monthlyCost.definedRows.length +
                      monthlyCost.otherRows.length >
                      8 ? (
                      <>
                        <div className={css.costRow}>
                          <button
                            type="button"
                            className={css.costRowExpand}
                            aria-expanded={expandedSections.costRows ? 'true' : 'false'}
                            onClick={() =>
                              expandedSections.costRows
                                ? showLess('costRows')
                                : showMore('costRows')
                            }
                          >
                            Others ({monthlyCost.otherRows.length} more)
                            <svg
                              className={classNames(css.costRowExpandIcon, {
                                [css.costRowExpandIconOpen]: expandedSections.costRows,
                              })}
                              width="12"
                              height="8"
                              viewBox="0 0 12 8"
                              aria-hidden="true"
                            >
                              <path
                                d="M1 1l5 5 5-5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                          {!expandedSections.costRows ? (
                            <strong>
                              {formatCurrencyValue(monthlyCost.otherTotal)}
                            </strong>
                          ) : null}
                        </div>
                        {expandedSections.costRows
                          ? monthlyCost.otherRows.map(row => (
                            <div
                              key={row.key}
                              className={classNames(
                                css.costRow,
                                css.costSubRow
                              )}
                            >
                              <span>{row.label}</span>
                              <strong>
                                {formatCurrencyValue(row.numericValue)}
                              </strong>
                            </div>
                          ))
                          : null}
                      </>
                    ) : (
                      monthlyCost.otherRows.map(row => (
                        <div key={row.key} className={css.costRow}>
                          <span>{row.label}</span>
                          <strong>{formatCurrencyValue(row.numericValue)}</strong>
                        </div>
                      ))
                    )}
                    {!monthlyCost.definedRows.length &&
                      !monthlyCost.otherRows.length ? (
                      <div className={css.costRow}>
                        <span>No monthly costs added yet</span>
                        <strong>$0</strong>
                      </div>
                    ) : null}
                    <div className={classNames(css.costRow, css.totalRow)}>
                      <span>Monthly total</span>
                      <strong>{monthlyTotal} USD</strong>
                    </div>
                  </div>

                  <div className={css.paymentCards}>
                    <article className={css.infoCard}>
                      <div className={css.infoCardHeading}>
                        {/* <SectionGlyph type="payment" /> */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="17"
                          height="12"
                          viewBox="0 0 17 12"
                          fill="none"
                        >
                          <path
                            d="M2.96875 0C2.18139 0 1.42628 0.312778 0.869527 0.869527C0.312778 1.42628 0 2.18139 0 2.96875V3.5625H16.625V2.96875C16.625 2.18139 16.3122 1.42628 15.7555 0.869527C15.1987 0.312778 14.4436 0 13.6562 0H2.96875ZM16.625 4.75H0V8.90625C0 9.69361 0.312778 10.4487 0.869527 11.0055C1.42628 11.5622 2.18139 11.875 2.96875 11.875H13.6562C14.4436 11.875 15.1987 11.5622 15.7555 11.0055C16.3122 10.4487 16.625 9.69361 16.625 8.90625V4.75ZM11.2812 8.3125H13.6562C13.8137 8.3125 13.9647 8.37506 14.0761 8.4864C14.1874 8.59775 14.25 8.74878 14.25 8.90625C14.25 9.06372 14.1874 9.21475 14.0761 9.3261C13.9647 9.43744 13.8137 9.5 13.6562 9.5H11.2812C11.1238 9.5 10.9728 9.43744 10.8614 9.3261C10.7501 9.21475 10.6875 9.06372 10.6875 8.90625C10.6875 8.74878 10.7501 8.59775 10.8614 8.4864C10.9728 8.37506 11.1238 8.3125 11.2812 8.3125Z"
                            fill="#712739"
                          />
                        </svg>
                        <h3>Accepted forms of monthly payment</h3>
                      </div>
                      <p>{paymentTypes.join(', ')}</p>
                    </article>

                    <article className={css.infoCard}>
                      <div className={css.infoCardHeading}>
                        {/* <SectionGlyph type="payment" /> */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="17"
                          height="12"
                          viewBox="0 0 17 12"
                          fill="none"
                        >
                          <path
                            d="M2.96875 0C2.18139 0 1.42628 0.312778 0.869527 0.869527C0.312778 1.42628 0 2.18139 0 2.96875V3.5625H16.625V2.96875C16.625 2.18139 16.3122 1.42628 15.7555 0.869527C15.1987 0.312778 14.4436 0 13.6562 0H2.96875ZM16.625 4.75H0V8.90625C0 9.69361 0.312778 10.4487 0.869527 11.0055C1.42628 11.5622 2.18139 11.875 2.96875 11.875H13.6562C14.4436 11.875 15.1987 11.5622 15.7555 11.0055C16.3122 10.4487 16.625 9.69361 16.625 8.90625V4.75ZM11.2812 8.3125H13.6562C13.8137 8.3125 13.9647 8.37506 14.0761 8.4864C14.1874 8.59775 14.25 8.74878 14.25 8.90625C14.25 9.06372 14.1874 9.21475 14.0761 9.3261C13.9647 9.43744 13.8137 9.5 13.6562 9.5H11.2812C11.1238 9.5 10.9728 9.43744 10.8614 9.3261C10.7501 9.21475 10.6875 9.06372 10.6875 8.90625C10.6875 8.74878 10.7501 8.59775 10.8614 8.4864C10.9728 8.37506 11.1238 8.3125 11.2812 8.3125Z"
                            fill="#712739"
                          />
                        </svg>
                        <h3>Home fees</h3>
                      </div>
                      {feeRows.length ? (
                        feeRows.map(row => (
                          <p key={row.label}>
                            {row.label}: <b>{row.value}</b>
                          </p>
                        ))
                      ) : (
                        <p>No home fees added yet.</p>
                      )}
                      <small>
                        For stays longer than one month, confirm the remaining
                        payment schedule with the host.
                      </small>
                    </article>

                    <article className={css.infoCard}>
                      <div className={css.infoCardHeading}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="19"
                          height="19"
                          viewBox="0 0 19 19"
                          fill="none"
                        >
                          <path
                            d="M16.625 4.98734C16.625 4.36192 16.2529 3.79192 15.6829 3.53859L10.1413 1.0765C9.72958 0.894421 9.2625 0.894421 8.85875 1.0765L3.31708 3.53859C2.74708 3.79192 2.375 4.36192 2.375 4.98734V8.70817C2.375 13.1019 5.415 17.2107 9.5 18.2082C11.3208 17.7648 12.9279 16.704 14.155 15.2711L11.685 12.8011C10.924 13.3041 10.0124 13.5284 9.10482 13.4361C8.19726 13.3438 7.34955 12.9404 6.70542 12.2944C5.96393 11.5518 5.54745 10.5453 5.54745 9.49588C5.54745 8.44646 5.96393 7.43994 6.70542 6.69734C7.44802 5.95585 8.45454 5.53937 9.50396 5.53937C10.5534 5.53937 11.5599 5.95585 12.3025 6.69734C12.9469 7.3424 13.3491 8.18993 13.4414 9.09702C13.5337 10.0041 13.3104 10.9153 12.8092 11.6769L15.105 13.9728C16.0629 12.4211 16.625 10.5923 16.625 8.70817V4.98734Z"
                            fill="#712739"
                          />
                          <path
                            d="M9.5 11.875C10.8117 11.875 11.875 10.8117 11.875 9.5C11.875 8.18832 10.8117 7.125 9.5 7.125C8.18832 7.125 7.125 8.18832 7.125 9.5C7.125 10.8117 8.18832 11.875 9.5 11.875Z"
                            fill="#712739"
                          />
                        </svg>
                        <h3>Cancellation policy</h3>
                      </div>
                      {cancellationPolicy ? (
                        <>
                          <strong className={css.policyTitle}>
                            {cancellationPolicy.label}
                          </strong>
                          <p className={css.cancelPolicydescription}>
                            {cancellationPolicy.description}{' '}
                            <button
                              className={css.policyLink}
                              type="button"
                              onClick={openPolicyModal}
                            >
                              View full policy
                            </button>
                          </p>
                        </>
                      ) : (
                        <p>No cancellation policy selected yet.</p>
                      )}
                    </article>
                  </div>
                </div>
              </section>

              <section
                className={classNames(css.newSection, css.highlightSection)}
              >
                <h2>Things to highlight</h2>
                <div className={css.highlightGrid}>
                  {renderHighlightCard(
                    'Preferences and care requests',
                    preferenceItems,
                    'preferences',
                    {
                      icon: 'preferences',
                      sections: preferenceGroupsWithIcons
                        .filter(group => group.items.length || group.note)
                        .map(group => ({
                          id: group.id,
                          title: group.name,
                          items: sortItemsAlphabetically(group.items),
                          note: group.note,
                        })),
                    }
                  )}
                  {renderHighlightCard(
                    'Safety and property',
                    safetyItems,
                    'safety',
                    { icon: 'safety' }
                  )}
                  {renderHighlightCard(
                    'Health and medical',
                    healthItems,
                    'health',
                    {
                      icon: 'health',
                      showDistance: true,
                      note: surroundingAdditionalInfo['health-and-medical'],
                    }
                  )}
                </div>
              </section>
            </div>
          </main>
        </div>
      </LayoutSingleColumn>

      <DetailModal
        detailModal={detailModal}
        onClose={closeDetailModal}
        onManageDisableScrolling={onManageDisableScrolling}
      />

      <Modal
        id="ListingPage.inquiry"
        contentClassName={css.inquiryModalContent}
        isOpen={isAuthenticated && inquiryModalOpen}
        onClose={() => setInquiryModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <InquiryForm
          className={css.inquiryForm}
          submitButtonWrapperClassName={css.inquirySubmitButtonWrapper}
          listingTitle={listingTitle}
          authorDisplayName={authorDisplayName}
          sendInquiryError={sendInquiryError}
          onSubmit={onSubmitInquiry}
          inProgress={sendInquiryInProgress}
        />
      </Modal>

    </Page>
  );
};

ListingPageComponent.defaultProps = {
  currentUser: null,
  inquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: null,
  sendInquiryError: null,
  lineItems: null,
  fetchLineItemsError: null,
  taxanomyIcons: [],
};

ListingPageComponent.propTypes = {
  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([
      LISTING_PAGE_DRAFT_VARIANT,
      LISTING_PAGE_PENDING_APPROVAL_VARIANT,
    ]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  // monthlyTimeSlots could be something like:
  // monthlyTimeSlots: {
  //   '2019-11': {
  //     timeSlots: [],
  //     fetchTimeSlotsInProgress: false,
  //     fetchTimeSlotsError: null,
  //   }
  // }
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  onToggleFavorite: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  taxanomyIcons: array,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    taxanomyIcons,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    taxanomyIcons,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params =>
    dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  onTransition: (txId, transitionName, params) =>
    dispatch(makeTransition(txId, transitionName, params)),
  onSendMessage: params => dispatch(sendMessage(params)),
  onToggleFavorite: data => dispatch(updateProfile(data)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(
  EnhancedListingPage
);

export default ListingPage;
